/**
 * Client for the feat-133 Batch-1 tenant-scoped reference-lookup endpoints
 * (restaurant-backend `routes/scoped_reads.py`, registered under `/api`).
 *
 * These REPLACE the raw `/admin/execute-select` reference-lookup queries that
 * consumers #1–5 built client-side (server list, locations list, available
 * months). Behavior-preserving security migration: each endpoint binds the
 * tenant/location filter server-side from the resolved `UserContext`
 * (Manager/Corporate pinned to own tenant, foreign tenant → 404 fail-closed),
 * and returns the SAME row shape the corresponding raw SELECT returned — so the
 * frontend's existing assembly/display logic is untouched. See ADR
 * `decisions/0001-scoped-read-endpoints.md`.
 *
 * Contract (confirmed with feat-133 backend): each endpoint returns a BARE JSON
 * array of row objects (NOT wrapped in `{ results: [...] }` like execute-select).
 *
 * Fake mode: ~~not exercised here — every consumer short-circuits to dummy data
 * (`config.environment === 'fake'` / `config.useDummyData`) BEFORE calling these,
 * exactly as it did before the migration.~~
 *
 * **STRUCK 2026-08-01 (feat-199) — THE SENTENCE ABOVE IS FALSE.** It is left
 * visible rather than deleted so a later reader meeting it quoted elsewhere can see
 * it was withdrawn, not that the file lost a claim.
 *
 * **NOT every consumer short-circuits.** The whole
 * `/api/admin/interaction-evaluations/*` family below — `getInteractionEvalStats`,
 * `judge-stats`, `worklist`, `locations`, `evaluator-stats`, `review`,
 * `disagreements`, and `rejections` — has no fake-mode branch at any layer:
 * `getArray`/`getObject` call `authedApiFetch` unconditionally and throw on non-2xx.
 * So under `npm run start:fake` those screens do **not** render dummy data; they
 * render their **ERROR** state.
 *
 * Two consequences, and the second is why this was worth striking rather than
 * leaving for later:
 *  - It is a useful lever: a fake-mode run is a free, un-mocked way to exercise an
 *    error path (feat-199 AC10's broken-arm).
 *  - It is a TRAP for anyone reviewing a screenshot or a mockup in fake mode, who
 *    will read "the request failed" as "there is nothing to review" — which is
 *    precisely the empty-vs-broken confusion feat-199 AC10 exists to prevent,
 *    occurring during the review OF that requirement.
 *
 * Struck as part of feat-199 because that feature ADDS a consumer to this family
 * (`getInteractionRejections`), which made the claim above more wrong than it was
 * found — leaving it would have been this change's own doing.
 */
import { config } from '../config/environment';
import { authHeader } from './authToken';
import { authedApiFetch } from './tokenRefresh';
// feat-186 — the composition wire types live in the (dependency-free) queries
// module so the bucket union / shares shape have a single source of truth shared
// by the service, the control, and the tests (avoids the two-sources-drift trap).
import type { BucketShares, ReviewBucket } from '../components/restaurant/admin/interactionEvaluationsQueries';

// ---------- Domain types (mirror the backend response models / raw SELECT shape) ----------

/** A restaurant location — `SELECT id, name FROM location`. */
export interface TenantLocation {
  id: string;
  name: string;
}

/**
 * A server row — mirrors the columns `ServerManagementView`'s raw
 * `server JOIN location` query returned. `features` arrives as a parsed JSON
 * value (backend coerces it, matching execute-select's JSON coercion);
 * `demo`/`test` are booleans; `location_name` is the joined location name.
 */
export interface TenantServer {
  id: string;
  name: string;
  location_id: string;
  features: unknown;
  demo: boolean;
  test: boolean;
  location_name: string;
}

/** A distinct year/month with performance data — `{ year, month }`. */
export interface AvailableMonth {
  year: number;
  month: number;
}

// ---------- HTTP plumbing (mirrors serverScorecardService) ----------

/**
 * feat-199 — the non-2xx throw, now carrying the STATUS and the PARSED body
 * alongside the message.
 *
 * `message` is byte-identical to what these helpers threw before (`API {path}
 * {status}: {text}`), and this extends `Error`, so every existing caller —
 * all of which do `e instanceof Error ? e.message : …` — behaves exactly as it
 * did. The additions are strictly extra information.
 *
 * WHY it exists: feat-199's queue endpoint returns **422 for two unrelated
 * reasons**. `interaction_type=drink_promotion` is 422 because that type is never
 * double-checked and can therefore never have a rejection (a real, permanent,
 * non-error state the UI must name honestly); `days=99` is 422 because the caller
 * sent garbage. Rendering "the request failed" for the first one is false — nothing
 * failed. Telling them apart off the message STRING would mean substring-matching
 * backend prose, a binding that breaks silently the day someone improves the
 * wording, and whose failure surfaces as the page showing the wrong one of two
 * states with green tests on both sides. So the discriminator has to be a FIELD.
 *
 * `body` is the parsed JSON when the response is JSON, else the raw text, else
 * null — never a throw from inside an error path.
 */
export class ScopedReadError extends Error {
  readonly status: number;
  readonly path: string;
  readonly body: unknown;

  constructor(path: string, status: number, text: string, body: unknown) {
    super(`API ${path} ${status}: ${text}`);
    this.name = 'ScopedReadError';
    this.status = status;
    this.path = path;
    this.body = body;
    // Required for `instanceof` to work on a subclassed Error once TS downlevels
    // to ES5 (CRA's target). Without it `e instanceof ScopedReadError` is false
    // and every discriminator below silently falls through to the generic branch.
    Object.setPrototypeOf(this, ScopedReadError.prototype);
  }
}

/** Parse an error body as JSON when it is JSON; otherwise keep the raw text. */
function parseErrorBody(text: string): unknown {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function backendUrl(path: string): string {
  const protocol =
    config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
}

/**
 * GET a scoped-read endpoint. Sends the Cognito bearer via `authHeader()`.
 * Throws on any non-2xx (including the 403/404 fail-closed responses) so callers
 * keep their existing try/catch → graceful-empty degrade.
 */
async function getArray<T>(path: string): Promise<T[]> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeader(),
  };
  const resp = await authedApiFetch(backendUrl(path), { method: 'GET', headers });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new ScopedReadError(path, resp.status, text, parseErrorBody(text));
  }
  return resp.json();
}

/**
 * GET a scoped-read endpoint returning a single object (e.g. `/shifts/{id}`).
 * Throws on any non-2xx — notably the 404 fail-closed on a foreign/absent shift,
 * so the caller's error path closes the PII surface (no partial render).
 */
async function getObject<T>(path: string): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeader(),
  };
  const resp = await authedApiFetch(backendUrl(path), { method: 'GET', headers });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new ScopedReadError(path, resp.status, text, parseErrorBody(text));
  }
  return resp.json();
}

// ---------- Endpoint clients ----------

/** GET /api/tenants/{tenantId}/locations → [{ id, name }] (ORDER BY name). */
export async function getTenantLocations(tenantId: string): Promise<TenantLocation[]> {
  return getArray<TenantLocation>(`/api/tenants/${encodeURIComponent(tenantId)}/locations`);
}

export interface TenantServersOptions {
  locationId?: string;
  excludeDemoTest?: boolean;
  hasShifts?: boolean;
  /**
   * Admin-only global listing across all tenants (PL D2 PRESERVE ruling). The
   * backend gates this on `is_admin()` and ignores it (fail-closed) for
   * non-admins. Callers pass the {@link ALL_TENANTS} sentinel as `tenantId`.
   */
  allTenants?: boolean;
}

/**
 * Path sentinel for the admin global (all-tenants) server listing:
 * `GET /api/tenants/all/servers?all_tenants=true`. Admin-only; a non-admin
 * hitting this path receives 404.
 */
export const ALL_TENANTS = 'all';

/**
 * GET /api/tenants/{tenantId}/servers → [{ id, name, location_id, features,
 * demo, test, location_name }] (ORDER BY name). With no options, returns ALL
 * servers for the (single) tenant, including demo/test. For the admin global
 * view, pass `ALL_TENANTS` as `tenantId` with `{ allTenants: true }`.
 */
export async function getTenantServers(
  tenantId: string,
  options: TenantServersOptions = {},
): Promise<TenantServer[]> {
  const params = new URLSearchParams();
  if (options.locationId) params.set('location_id', options.locationId);
  if (options.excludeDemoTest) params.set('exclude_demo_test', 'true');
  if (options.hasShifts) params.set('has_shifts', 'true');
  if (options.allTenants) params.set('all_tenants', 'true');
  const qs = params.toString();
  return getArray<TenantServer>(
    `/api/tenants/${encodeURIComponent(tenantId)}/servers${qs ? `?${qs}` : ''}`,
  );
}

export interface AvailableMonthsOptions {
  locationId?: string;
  /** Preferred server key — globally unique, tenant-ownership-checked server-side. */
  serverId?: string;
  /**
   * Fallback server key when the id is unavailable (e.g. ServerPerformanceHistory
   * reached without `serverId` in route-state). Bound + tenant-scoped server-side,
   * so it is not a cross-tenant leak. Ignored when `serverId` is also supplied.
   */
  serverName?: string;
}

/**
 * GET /api/tenants/{tenantId}/available-months → [{ year, month }]
 * (year DESC, month DESC). Optional `location_id` / `server_id` / `server_name`
 * narrow the set; the backend preserves the exact predicate (US/Pacific month
 * bucketing, joined verified interactions) so the rows match the prior raw query.
 */
export async function getTenantAvailableMonths(
  tenantId: string,
  options: AvailableMonthsOptions = {},
): Promise<AvailableMonth[]> {
  const params = new URLSearchParams();
  if (options.locationId) params.set('location_id', options.locationId);
  if (options.serverId) {
    params.set('server_id', options.serverId);
  } else if (options.serverName) {
    params.set('server_name', options.serverName);
  }
  const qs = params.toString();
  return getArray<AvailableMonth>(
    `/api/tenants/${encodeURIComponent(tenantId)}/available-months${qs ? `?${qs}` : ''}`,
  );
}

// ============================================================================
// feat-133 Batch-2 — shift transcript / detail (consumers #6–10).
//
// Each per-shift endpoint is authorized server-side by `_authorize_shift`
// (shift → server → tenant; foreign/absent shift → 404 fail-closed — closing the
// raw-transcript PII leak), then runs the VERBATIM original query %s-bound, so
// the rows are byte-identical to the prior /admin/execute-select output. The FE
// assembly (groupOrderRows nesting, timeline render, pagination sentinel) is
// unchanged — only the data SOURCE swaps. See ADR 0002.
//
// Row shapes mirror the original SELECT columns exactly; loose field types match
// how the raw `results` rows were consumed as `any`. DECIMAL money cols
// (final_total/net_sales/price) arrive as JSON numbers (byte-identical to
// execute-select today); *_seconds are floats — callers already parseFloat both.
// ============================================================================

/** Single-object shift header (mirrors ShiftTranscriptView's Q1 columns). */
export interface ShiftDetail {
  server_name: string | null;
  prompt_template_version_id: string | null;
  shift_role: string | null;
  shift_app_version: string | null;
  shift_app_build: string | null;
  shift_patch_number: string | null;
  shift_device_model: string | null;
  shift_summary_json: unknown; // parsed JSON (string-or-object tolerated by callers)
  device_name: string | null;
  shift_created: string;
  shift_ended: string | null;
  // feat-319 — additive, all nullable (LEFT JOINs on the backend).
  server_default_role?: string | null;
  /** COALESCE(shift.role, server.default_role). */
  effective_role?: string | null;
  /** tenant_role.display_name for (shift's tenant, effective_role). */
  role_display_name?: string | null;
  prompt_template_id?: string | null;
  prompt_template_name?: string | null;
  prompt_template_role?: string | null;
  prompt_template_tenant_id?: string | null;
  prompt_version_number?: number | string | null;
  /** May arrive as 0/1 from MySQL — coerce to boolean at the edge. */
  prompt_version_is_active?: boolean | number | null;
}

export interface TranscriptPartRow {
  id: number | string;
  speaker: string | number;
  start_time: number;
  text: string;
  created: string;
}

export interface ShiftEventRow {
  id: number | string;
  event_type: string;
  event_data: unknown; // parsed JSON
  created: string;
}

export interface ShiftInteractionRow {
  id: number | string;
  start_time: number;
  end_time: number;
  interaction_type: string;
  data: unknown; // parsed JSON
  created: string;
  correct_human_verified: boolean | null;
}

export interface GemTransactionRow {
  source_id: number | string;
  amount: number | string;
  description: string | null;
}

/** Flat Revel row (one per item×link) — nested client-side by groupOrderRows. */
export interface PosOrderRow {
  order_no: number;
  shift_offset_seconds: number | string;
  transcript_part_id: number | string | null;
  first_opened: string | null;
  last_closed: string | null;
  final_total: number | string;
  net_sales: number | string;
  item_id: number | string | null;
  item_name: string | null;
  price: number | string | null;
  quantity: number | string | null;
  shift_interaction_id: number | string | null;
  match_rule: string | null;
  time_diff_seconds: number | string | null;
  match_explanation: string | null;
}

export interface TenantShiftRow {
  id: string;
  server_id: string;
  server_name: string | null;
  started: string;
  has_notifications: boolean | number;
  notes: string | null;
}

/** GET /api/shifts/{shiftId} → single shift header object (404 on foreign shift). */
export async function getShift(shiftId: string): Promise<ShiftDetail> {
  return getObject<ShiftDetail>(`/api/shifts/${encodeURIComponent(shiftId)}`);
}

export interface TranscriptWindow {
  /** Inclusive start second (>= start). */
  start?: number;
  /** Inclusive end second (<= end). */
  end?: number;
}

/**
 * GET /api/shifts/{shiftId}/transcript?start=&end= → transcript parts
 * (ORDER BY start_time, sort_order). Optional inclusive numeric-seconds window;
 * absent = full transcript. Superset shape serves #6 (full), #7 (start_time as
 * seconds), #8/#9 (windowed).
 */
export async function getShiftTranscript(
  shiftId: string,
  window: TranscriptWindow = {},
): Promise<TranscriptPartRow[]> {
  const params = new URLSearchParams();
  if (window.start != null) params.set('start', String(window.start));
  if (window.end != null) params.set('end', String(window.end));
  const qs = params.toString();
  return getArray<TranscriptPartRow>(
    `/api/shifts/${encodeURIComponent(shiftId)}/transcript${qs ? `?${qs}` : ''}`,
  );
}

/**
 * GET /api/shifts/{shiftId}/events → full shift-event list (ORDER BY created).
 * The connection event is derived client-side (first event_type==='client_connected').
 */
export async function getShiftEvents(shiftId: string): Promise<ShiftEventRow[]> {
  return getArray<ShiftEventRow>(`/api/shifts/${encodeURIComponent(shiftId)}/events`);
}

/** GET /api/shifts/{shiftId}/interactions → interaction list (ORDER BY start_time). */
export async function getShiftInteractions(shiftId: string): Promise<ShiftInteractionRow[]> {
  return getArray<ShiftInteractionRow>(`/api/shifts/${encodeURIComponent(shiftId)}/interactions`);
}

/** GET /api/shifts/{shiftId}/gem-transactions → gem rows (tolerant/non-fatal). */
export async function getShiftGemTransactions(shiftId: string): Promise<GemTransactionRow[]> {
  return getArray<GemTransactionRow>(
    `/api/shifts/${encodeURIComponent(shiftId)}/gem-transactions`,
  );
}

/**
 * GET /api/shifts/{shiftId}/pos-orders → FLAT Revel rows (one per item×link,
 * ORDER BY shift_offset_seconds, roi.id). Preserves the revel_* source (DP2).
 * The caller's groupOrderRows() nests these into order → items → links.
 */
export async function getShiftPosOrders(shiftId: string): Promise<PosOrderRow[]> {
  return getArray<PosOrderRow>(`/api/shifts/${encodeURIComponent(shiftId)}/pos-orders`);
}

export interface TenantShiftsOptions {
  serverId?: string;
  /** 0-based page index (pass currentPage - 1). */
  page?: number;
  /** Page size (default 100 server-side); the endpoint returns page_size + 1 (sentinel). */
  pageSize?: number;
  /** Admin-only global listing — pass ALL_TENANTS as tenantId with this true. */
  allTenants?: boolean;
}

/**
 * GET /api/tenants/{tenantId}/shifts?server_id=&page=&page_size= → paginated
 * shift list (ORDER BY started DESC). Returns page_size + 1 rows (the sentinel),
 * so the caller keeps `hasNextPage = rows.length > pageSize` + `slice(0, pageSize)`.
 * Admin global view: pass ALL_TENANTS + { allTenants: true } → /tenants/all/shifts.
 */
export async function getTenantShifts(
  tenantId: string,
  options: TenantShiftsOptions = {},
): Promise<TenantShiftRow[]> {
  const params = new URLSearchParams();
  if (options.serverId) params.set('server_id', options.serverId);
  if (options.page != null) params.set('page', String(options.page));
  if (options.pageSize != null) params.set('page_size', String(options.pageSize));
  if (options.allTenants) params.set('all_tenants', 'true');
  const qs = params.toString();
  return getArray<TenantShiftRow>(
    `/api/tenants/${encodeURIComponent(tenantId)}/shifts${qs ? `?${qs}` : ''}`,
  );
}

// ============================================================================
// feat-133 Batch-3 — scorecard aggregation (consumer #11 ServerLeaderboardService).
//
// The 6 /tenants/{tenant_id}/server-stats/* endpoints replace the 6 raw query
// shapes ServerLeaderboardService.getServerLeaderboardData built client-side.
// Tenant/location are bound server-side from UserContext; the server-grain
// endpoints scope on server.tenant_id/location_id, and the shift-batch-grain
// endpoints re-verify each shift_id via shift→server tenant (a foreign shift_id
// contributes ZERO rows — the cross-tenant aggregation leak is closed). There is
// NO `all` sentinel (ADR 0003 DP-3a option-b): callers always pass a specific
// tenant_id from auth context.
//
// These return `any[]` (mirroring the prior `executeQuery` → `data.results: any`)
// so ServerLeaderboardService's ServerStats assembly / batching / groupByDate is
// left untouched.
//
// ⚠️ feat-218 NARROWED THE BYTE-IDENTITY GUARANTEE. It no longer holds for the
// `criteria` row shape below (nor for `perf-criteria`) — those two responses are
// now deliberately reduced projections, not byte-for-byte replays of the legacy
// execute-select output. It DOES still hold for every other row shape here and
// for the other Batch-3/Batch-4 endpoints; their A/B tests are the controls that
// prove it. Do not generalise the exception.
//
// Row shapes (documented, backend contract; money DECIMALs arrive as JSON numbers):
//   shifts        → { shift_id, server_id, shift_created, shift_ended, interactions_count, server_created }
//   servers       → { server_id, server_name, revel_server_id, role, location_id, location_name }
//   premium-sales → premium_sales rollup (revel_server_id, *_sales, premium_conversion_rate, sale_date, …)
//   criteria      → { shift_id, start_time, end_time, interaction_type, interaction_data }
//                   interaction_data is NARROWED to { service_criteria: [{ type, fulfilled }] }.
//                   feat-218 (D7/C′) — ⏳ NOT YET IMPLEMENTED as of this comment; the backend
//                   half lands with the :1474 slice. Until then this endpoint still returns all six
//                   columns and the full blob. If you are reading this and the response still has
//                   original_transcript, the backend half slipped — that is a bug, not a doc error.
//                   server_id, interaction_id, and the blob's
//                   original_transcript / reasoning / quality_level are no longer sent.
//                   NOTE: this endpoint has NEVER been measured in prod — the 5.5 MiB
//                   Sentry observation is on `perf-criteria`, not this one. It was
//                   trimmed on the same-shape reasoning plus a PII argument, not on an
//                   observation of its own size. Measured on 18 real annotator blobs the
//                   dropped keys are ~60% of blob bytes (reasoning 40.7%,
//                   original_transcript 19.4%); original_transcript is verbatim customer
//                   speech (HIGH PII per scripts/scrub_staging.sql) that was crossing the
//                   public internet on every leaderboard + Server-Summary page load.
//                   start_time/end_time are RETAINED deliberately: ServerSummaryView's
//                   openInteractionDetails guards on `typeof … === 'number'` before
//                   fetching the transcript window, and falls back to "No transcript
//                   available" without them.
//   revel-order-counts   → { shift_id, order_count }
//   revel-attribution    → { server_id, match_rule, purchase_count, total_value }
// shift_ids / server_ids are passed as comma-separated raw ids (backend strips
// any quotes defensively).
// ============================================================================

const serverStatsBase = (tenantId: string) =>
  `/api/tenants/${encodeURIComponent(tenantId)}/server-stats`;

export interface ServerStatsShiftsOptions {
  start?: string;
  end?: string;
  locationId?: string;
  serverIds?: string[];
}

/** GET …/server-stats/shifts — per-shift rollup rows (replaces query #1). */
export async function getServerStatsShifts(
  tenantId: string,
  options: ServerStatsShiftsOptions = {},
): Promise<any[]> {
  const params = new URLSearchParams();
  if (options.start) params.set('start', options.start);
  if (options.end) params.set('end', options.end);
  if (options.locationId) params.set('location_id', options.locationId);
  if (options.serverIds) {
    // Repeated query params (?server_ids=a&server_ids=b) — the canonical transport.
    options.serverIds.forEach((id) => params.append('server_ids', id));
  }
  const qs = params.toString();
  return getArray<any>(`${serverStatsBase(tenantId)}/shifts${qs ? `?${qs}` : ''}`);
}

export interface ServerStatsServersOptions {
  serverIds?: string[];
  locationId?: string;
}

/** GET …/server-stats/servers — server metadata rows (replaces query #3/F). */
export async function getServerStatsServers(
  tenantId: string,
  options: ServerStatsServersOptions = {},
): Promise<any[]> {
  const params = new URLSearchParams();
  if (options.serverIds) {
    // Repeated query params (?server_ids=a&server_ids=b) — the canonical transport.
    options.serverIds.forEach((id) => params.append('server_ids', id));
  }
  if (options.locationId) params.set('location_id', options.locationId);
  const qs = params.toString();
  return getArray<any>(`${serverStatsBase(tenantId)}/servers${qs ? `?${qs}` : ''}`);
}

export interface ServerStatsPremiumSalesOptions {
  start?: string;
  end?: string;
  locationId?: string;
}

/** GET …/server-stats/premium-sales — Revel premium/regular rollup (replaces #4/B). */
export async function getServerStatsPremiumSales(
  tenantId: string,
  options: ServerStatsPremiumSalesOptions = {},
): Promise<any[]> {
  const params = new URLSearchParams();
  if (options.start) params.set('start', options.start);
  if (options.end) params.set('end', options.end);
  if (options.locationId) params.set('location_id', options.locationId);
  const qs = params.toString();
  return getArray<any>(`${serverStatsBase(tenantId)}/premium-sales${qs ? `?${qs}` : ''}`);
}

/** GET …/server-stats/criteria?shift_ids= — interaction/criteria rows (replaces #2/C). */
export async function getServerStatsCriteria(
  tenantId: string,
  shiftIds: string[],
): Promise<any[]> {
  const params = new URLSearchParams();
  // Repeated query params (?shift_ids=a&shift_ids=b) — the canonical transport.
  shiftIds.forEach((id) => params.append('shift_ids', id));
  return getArray<any>(`${serverStatsBase(tenantId)}/criteria?${params.toString()}`);
}

/** GET …/server-stats/revel-order-counts?shift_ids= — per-shift order counts (replaces #5/D). */
export async function getServerStatsRevelOrderCounts(
  tenantId: string,
  shiftIds: string[],
): Promise<any[]> {
  const params = new URLSearchParams();
  // Repeated query params (?shift_ids=a&shift_ids=b) — the canonical transport.
  shiftIds.forEach((id) => params.append('shift_ids', id));
  return getArray<any>(`${serverStatsBase(tenantId)}/revel-order-counts?${params.toString()}`);
}

/** GET …/server-stats/revel-attribution?shift_ids= — per-server×match_rule attribution (replaces #6/E). */
export async function getServerStatsRevelAttribution(
  tenantId: string,
  shiftIds: string[],
): Promise<any[]> {
  const params = new URLSearchParams();
  // Repeated query params (?shift_ids=a&shift_ids=b) — the canonical transport.
  shiftIds.forEach((id) => params.append('shift_ids', id));
  return getArray<any>(`${serverStatsBase(tenantId)}/revel-attribution?${params.toString()}`);
}

// ============================================================================
// feat-133 Batch-4 — single-server performance history (consumer #12
// ServerPerformanceHistoryService.getServerPerformanceHistory).
//
// Server-scoped endpoints keyed by server_id in the PATH — the server is verified
// ∈ tenant server-side (foreign/absent server_id → 404), which ELIMINATES the
// prior injection-risk by-name lookup (server names aren't unique across tenants;
// ADR 0004 DP-4a). The FE keys by the nav serverData.serverId + auth-context
// tenant. Shift-batch endpoints re-verify each shift_id via shift→server tenant
// (foreign shift_id → 0 rows). Revel order-counts REUSES the Batch-3 endpoint
// (byte-identical to #12's). Returns any[] so the ServerPerformanceHistoryEntry
// assembly stays untouched. Row shapes (backend contract; money = JSON numbers):
//   perf-shifts            → { shift_id, shift_created, shift_ended, shift_type, role, prompt_template_version_id, interactions_count }
//   perf-criteria          → { shift_id, interaction_type, interaction_data }
//                            interaction_data is NARROWED to { service_criteria: [{ type, fulfilled }] }.
//                            feat-218 (D7 — this endpoint's shape was settled at D6 and is UNCHANGED
//                            by D7; both comments here cite D7 so there is one contract, not two):
//                            interaction_id, start_time, and end_time are no longer
//                            sent — ServerPerformanceHistoryService reads none of them; it touches
//                            only shift_id (grouping), interaction_type (the drink_promotion branch),
//                            and service_criteria[].{type,fulfilled} (counts + the discoveredKeys
//                            dynamic-column path). The full LLM blob was 5.5 MiB uncompressed for one
//                            30-shift batch.
//                            NOTE: unlike server-stats/criteria, this endpoint drops start_time/end_time
//                            because no consumer of THIS response opens a transcript modal.
//   perf-revel-attribution → { shift_id, match_rule, purchase_count, total_value } (grouped by SHIFT — #12 keys revelAttributionMap by shift_id)
// ============================================================================

const perfBase = (tenantId: string, serverId: string) =>
  `/api/tenants/${encodeURIComponent(tenantId)}/servers/${encodeURIComponent(serverId)}`;

export interface ServerPerfShiftsOptions {
  start?: string;
  end?: string;
}

/** GET …/servers/{serverId}/perf-shifts?start=&end= — per-shift rows (replaces #12 shifts). */
export async function getServerPerfShifts(
  tenantId: string,
  serverId: string,
  options: ServerPerfShiftsOptions = {},
): Promise<any[]> {
  const params = new URLSearchParams();
  if (options.start) params.set('start', options.start);
  if (options.end) params.set('end', options.end);
  const qs = params.toString();
  return getArray<any>(`${perfBase(tenantId, serverId)}/perf-shifts${qs ? `?${qs}` : ''}`);
}

/** GET …/servers/{serverId}/perf-criteria?shift_ids= — aliased criteria rows (replaces #12 criteria). */
export async function getServerPerfCriteria(
  tenantId: string,
  serverId: string,
  shiftIds: string[],
): Promise<any[]> {
  const params = new URLSearchParams();
  shiftIds.forEach((id) => params.append('shift_ids', id));
  return getArray<any>(`${perfBase(tenantId, serverId)}/perf-criteria?${params.toString()}`);
}

/** GET …/servers/{serverId}/perf-revel-attribution?shift_ids= — per-SHIFT attribution (replaces #12 revel-attribution). */
export async function getServerPerfRevelAttribution(
  tenantId: string,
  serverId: string,
  shiftIds: string[],
): Promise<any[]> {
  const params = new URLSearchParams();
  shiftIds.forEach((id) => params.append('shift_ids', id));
  return getArray<any>(
    `${perfBase(tenantId, serverId)}/perf-revel-attribution?${params.toString()}`,
  );
}

// ============================================================================
// feat-133 Batch-5 — POS/upsell (#13 UpsellDashboard) + interaction-evaluations
// (#14 InteractionEvaluations). TWO distinct gates (ADR 0005):
//   • upsell-dashboard/*  → tenant-scoped (require_scorecard_read + location pin);
//     DP2 preserves revel_*. Money DECIMALs arrive as JSON numbers.
//   • admin/interaction-evaluations/*  → require_admin, NOT tenant-scoped (the
//     feat-130 cross-tenant admin eval tool; DP-5a). `days` ∈ {7,14,30} (else
//     400), `location_id` UUID-validated server-side (else 400), all %s-bound —
//     the migration kills the arbitrary-SQL/injection primitive.
// All bare arrays; the consumers' assembly is unchanged.
// ============================================================================

// ---------- #13 UpsellDashboard (tenant-scoped) ----------

const upsellBase = (tenantId: string) =>
  `/api/tenants/${encodeURIComponent(tenantId)}/upsell-dashboard`;

/** GET …/upsell-dashboard/matched-events?date= — q1 matched Revel/upsell events. */
export async function getUpsellMatchedEvents(
  tenantId: string,
  options: { date: string },
): Promise<any[]> {
  const params = new URLSearchParams();
  params.set('date', options.date);
  return getArray<any>(`${upsellBase(tenantId)}/matched-events?${params.toString()}`);
}

/** GET …/upsell-dashboard/daily-revenue?start=&end= — q2 per-day revenue rollup. */
export async function getUpsellDailyRevenue(
  tenantId: string,
  options: { start: string; end: string },
): Promise<any[]> {
  const params = new URLSearchParams();
  params.set('start', options.start);
  params.set('end', options.end);
  return getArray<any>(`${upsellBase(tenantId)}/daily-revenue?${params.toString()}`);
}

/** GET …/upsell-dashboard/location-totals?date= — q3 per-location upsell counts. */
export async function getUpsellLocationTotals(
  tenantId: string,
  options: { date: string },
): Promise<any[]> {
  const params = new URLSearchParams();
  params.set('date', options.date);
  return getArray<any>(`${upsellBase(tenantId)}/location-totals?${params.toString()}`);
}

// ---------- #14 InteractionEvaluations (admin-only, NOT tenant-scoped) ----------

const INTERACTION_EVAL_BASE = '/api/admin/interaction-evaluations';

export interface InteractionEvalOptions {
  /** Window in days — must be one of {7,14,30} (validated server-side). */
  days?: number;
  /** Optional UUID location filter (validated server-side). */
  locationId?: string;
  /**
   * feat-196 (ADR 0001 §4.3) — "Live clients only". DEFAULT ON, and ON is
   * expressed by ABSENCE on the wire. Pass `false` to lift the live-tenant scope;
   * omit or pass `true` to keep it. See {@link setLiveOnlyParam} for the emit rule.
   */
  liveOnly?: boolean;
}

/**
 * feat-196 (ADR 0001 §4.3) — write the `live_only` query param, or deliberately
 * don't. ONE helper rather than the same three lines in seven wrappers, because
 * every clause below is load-bearing and a hand-copy is where one of them gets
 * dropped.
 *
 * The rule, copied byte-for-byte from feat-188's `exclude_human_reviewed` (the
 * proven default-ON-boolean shape on this exact surface):
 *
 *  - **ON is expressed by ABSENCE.** Never emit `live_only=true`. That keeps
 *    exactly ONE canonical URL per state, so a bare or shared link filters to
 *    live-only — which is D6's safe default — and two URLs can't encode "ON"
 *    ambiguously.
 *  - **OFF is the single canonical literal `live_only=false`.**
 *  - **Never emit a valueless `live_only=`.** The architect measured this against
 *    the pinned backend stack: a bare `?live_only=` returns **422 bool_parsing**.
 *    That is a page-breaker, not a style preference — hence the strict `=== false`
 *    test rather than a falsy check, which would emit for `undefined`.
 *  - **`set`, never `append`** — duplicate keys take the LAST value server-side
 *    (also measured), so an append could silently invert the filter.
 *
 * The 422 contract (AC5) is for third-party and hand-edited callers. The frontend
 * normalizes before sending and therefore never transmits a value the backend
 * would reject — do NOT "helpfully" pass a garbage value through to surface the
 * 422, which is the opposite of how `exclude_human_reviewed` behaves.
 */
export function setLiveOnlyParam(params: URLSearchParams, liveOnly?: boolean): void {
  if (liveOnly === false) params.set('live_only', 'false');
}

/** GET /api/admin/interaction-evaluations/stats?days=&location_id= — per-type eval counts. */
export async function getInteractionEvalStats(
  options: InteractionEvalOptions = {},
): Promise<any[]> {
  const params = new URLSearchParams();
  if (options.days != null) params.set('days', String(options.days));
  if (options.locationId) params.set('location_id', options.locationId);
  setLiveOnlyParam(params, options.liveOnly);
  const qs = params.toString();
  return getArray<any>(`${INTERACTION_EVAL_BASE}/stats${qs ? `?${qs}` : ''}`);
}

/**
 * GET /api/admin/interaction-evaluations/judge-stats?days=&location_id= — feat-156
 * Interaction Judge validation. A NEW admin-scoped sibling of /stats (ADR 0001 §6),
 * kept separate so the existing endpoint stays untouched for the security gate.
 * Returns per-type Discarded % (live double-check) + Judge agreement % (offline
 * judge) for greeting + upsell, plus raw counts. Percentages are computed
 * server-side (null on zero denominator); the FE merges by interaction_type and
 * renders '—' on null. Empty until the interaction_judgement table is populated.
 */
export async function getInteractionEvalJudgeStats(
  options: InteractionEvalOptions = {},
): Promise<any[]> {
  const params = new URLSearchParams();
  if (options.days != null) params.set('days', String(options.days));
  if (options.locationId) params.set('location_id', options.locationId);
  setLiveOnlyParam(params, options.liveOnly);
  const qs = params.toString();
  return getArray<any>(`${INTERACTION_EVAL_BASE}/judge-stats${qs ? `?${qs}` : ''}`);
}

/** GET /api/admin/interaction-evaluations/worklist?days=&location_id= — unverified-shift worklist. */
export async function getInteractionEvalWorklist(
  options: InteractionEvalOptions = {},
): Promise<any[]> {
  const params = new URLSearchParams();
  if (options.days != null) params.set('days', String(options.days));
  if (options.locationId) params.set('location_id', options.locationId);
  setLiveOnlyParam(params, options.liveOnly);
  const qs = params.toString();
  return getArray<any>(`${INTERACTION_EVAL_BASE}/worklist${qs ? `?${qs}` : ''}`);
}

/**
 * GET /api/admin/interaction-evaluations/locations — all-tenants location list (admin filter).
 *
 * feat-196 (D3) — this endpoint takes `live_only` too, and the requirement is that
 * the dropdown must not even OFFER a location belonging to a non-live client.
 * Without it the filter would be honest about rows but not about choices: you could
 * pick a location and get a guaranteed-empty table with no explanation.
 *
 * Note this is the ONE endpoint in the family that scopes via `location.tenant_id`
 * rather than `server.tenant_id` — it has no `server` in scope (ADR 0001 §1). The
 * two agree wherever INV-1 holds; §1.4 states when they don't and why that's
 * accepted. Nothing on the FE depends on the difference.
 */
export async function getInteractionEvalLocations(
  options: InteractionEvalOptions = {},
): Promise<any[]> {
  const params = new URLSearchParams();
  setLiveOnlyParam(params, options.liveOnly);
  const qs = params.toString();
  return getArray<any>(`${INTERACTION_EVAL_BASE}/locations${qs ? `?${qs}` : ''}`);
}

/**
 * GET /api/admin/interaction-evaluations/evaluator-stats?days=&location_id= — feat-191
 * Part E. Per-grader throughput and agreement, grouped by `shift_interaction.verified_by`.
 *
 * THE ONE ENDPOINT IN THIS FAMILY THAT STAYS `require_admin`. Its six siblings moved
 * to `require_evaluator_or_admin` for feat-191; this one must NOT, because it is
 * oversight OF evaluators and it names other graders by email. It 403s for an
 * evaluator token (AC13), and the panel is absent for them in the UI — the 403 is
 * the boundary, the hidden panel is not.
 *
 * Returns an OBJECT, not an array (unlike its siblings): the window basis and the
 * suppression threshold are envelope-level facts, not per-row ones.
 *
 * Two response fields carry meaning that is easy to get wrong:
 *  - `agreement_pct` is null whenever `agreement_denominator < min_agreement_denominator`,
 *    even though the denominator is non-zero. That is deliberate suppression, not
 *    missing data. NEVER re-derive it from `agreement_matches / agreement_denominator`;
 *    `agreement_matches` is sent so the ratio can be AUDITED, not re-computed.
 *  - `window_basis` is `"verified_at"` — when the GRADING happened. Every sibling
 *    windows on `s.created`, when the INTERACTION happened. The same "last 7 days"
 *    label on this panel and on the stat cards above it answer different questions
 *    and will not reconcile. That is by design, and the field exists so the UI can
 *    say so instead of leaving someone to file the mismatch as a bug.
 */
export async function getInteractionEvalEvaluatorStats(
  options: InteractionEvalOptions = {},
): Promise<any> {
  const params = new URLSearchParams();
  if (options.days != null) params.set('days', String(options.days));
  if (options.locationId) params.set('location_id', options.locationId);
  setLiveOnlyParam(params, options.liveOnly);
  const qs = params.toString();
  return getObject<any>(`${INTERACTION_EVAL_BASE}/evaluator-stats${qs ? `?${qs}` : ''}`);
}

// ============================================================================
// feat-159 — type-first interaction review (ADR 0001 §D4). A NEW admin-scoped
// sibling of the eval reads: GET /api/admin/interaction-evaluations/review
// returns ONE type's interactions (the reviewer picks a single interaction_type),
// each with the transcript excerpt the AI saw (feat-183: the tight interaction span
// [floor(start), ceil(end)] the judge scored), the annotator's grade + reasoning (INSIDE `data`), the
// judgement rows (offline judge + live double-check, feat-156), and the current
// human-verified status. Same gate as the family: require_admin, cross-tenant
// (DP-5a), %s-bound, windowed by `_eval_scope(days, location_id)`.
//
// Response is an ENVELOPE (not a bare array) so the FE can page without a total:
//   { interaction_type, days, location_id, limit, offset, count, items:[...] }
// `count` = rows on THIS page; request the next page when count == limit.
//
// The write side (Correct/Incorrect) reuses the existing feat-131 verify path
// (PUT /shift-interactions/{id}) via RestaurantBackend.apiCall — NOT this module.
//
// Field shapes mirror the D4 contract. Rendered verbatim — the FE never recomputes
// the judge verdict/grade (feedback_metrics_single_source).
// ============================================================================

/** One speaker-labeled transcript part from the interaction span the AI scored. */
export interface ReviewTranscriptPart {
  /** Speaker index (diarization label) — an int. */
  speaker: number;
  /** Seconds from shift start (same basis as the interaction's start/end). */
  start_time: number;
  text: string;
}

/**
 * One judgement row (feat-156 `interaction_judgement`). `source` distinguishes the
 * independent offline judge from the live double-check; verbatim, never recomputed.
 */
export interface ReviewJudgement {
  source: 'offline_judge' | 'live_double_check' | string;
  // feat-174 — `uncertain` is a first-class third verdict, distinct from
  // `rejected`: the window was genuinely insufficient (garbled / clipped /
  // missing context), NOT specific evidence the annotator was wrong. Rendered
  // verbatim (never recomputed); the open union keeps forward-compat.
  verdict: 'confirmed' | 'rejected' | 'uncertain' | 'error' | string | null;
  grade: string | null;
  reasoning: string | null;
  judge_model?: string | null;
  prompt_version_id?: string | null;
  created?: string | null;
  // feat-174 (ADR 0001 B4) — the criteria the judge's rubric CONTAINED for this
  // judgement, reconstructed server-side per `prompt_version_id`. Surfaced in the
  // review detail so a reviewer can tell a genuine content disagreement from an
  // "unknown-criterion" plumbing bug (the annotator's fulfilled criterion absent
  // from this list). Optional — older judgements / endpoints omit it.
  rubric_criteria?: { key: string; label: string }[];
}

/**
 * feat-160 (ADR 0001) — a server-classified disagreement pair. The FE renders
 * these verbatim as reason chips and NEVER recomputes whether a pair fired.
 *   • `annotator_vs_offline` — the independent offline judge rejected an
 *     interaction the annotator asserted happened.
 *   • `annotator_vs_live` — the annotator's own real-time double-check rejected
 *     its initial annotation.
 *   • `human_vs_offline` — the human verification conflicts with the offline judge.
 *   • `offline_uncertain` (feat-174) — the offline judge returned `uncertain`.
 *     NOT a conflict: it routes the interaction into the human-review worklist
 *     (uncertain is neither agreement nor disagreement). Rendered as its own
 *     "needs review" chip, visually distinct from the conflict tokens above.
 */
export type DisagreementToken =
  | 'annotator_vs_offline'
  | 'annotator_vs_live'
  | 'human_vs_offline'
  | 'offline_uncertain';

/** One reviewable interaction of a single type (D4 item shape). */
export interface InteractionReviewItem {
  id: string;
  shift_id: string;
  interaction_type: string;
  start_time: number;
  /** Nullable — some interactions have no end bound. */
  end_time: number | null;
  created: string | null;
  /**
   * Raw annotator `data` JSON — the grade + reasoning live INSIDE here
   * (`data.grade`, `data.reasoning`); there is NO top-level grade column.
   */
  data: { reasoning?: string | null; grade?: string | null; [k: string]: unknown } | null;
  correct_human_verified: boolean | null;
  verified_by: string | null;
  verified_at: string | null;
  /** 0..n judgement rows; [] is valid (e.g. drink_promotion → "not judged"). */
  judgements: ReviewJudgement[];
  /** The interaction span the AI scored ([floor(start), ceil(end)]), speaker-labeled, time-ordered. */
  transcript: ReviewTranscriptPart[];
  // D4a attribution (approved 2026-07-15) — cross-tenant review header WHO/WHERE +
  // deep-link to the full shift transcript. Kept optional so the FE degrades if a
  // row omits them.
  server_id?: string | null;
  server_name?: string | null;
  location_name?: string | null;
  /**
   * feat-160 (ADR 0001) — which annotator/judge/human disagreement pair(s) fired,
   * classified SERVER-SIDE (never recomputed on the FE). Tokens from a fixed set:
   * `annotator_vs_offline` | `annotator_vs_live` | `human_vs_offline`. `[]` (or
   * absent, pre-deploy) = no disagreement. Rendered as reason chips verbatim.
   */
  disagreements?: DisagreementToken[];
  /**
   * feat-186 (ADR 0001 §D-B/§D-J) — the SERVER-AUTHORITATIVE queue-composition
   * bucket this item was drawn from. REQUIRED non-null on every JUDGED item
   * (initial_greeting / server_upsell), including graceful-degrade fills. ABSENT
   * (or null) for `drink_promotion` (never judged → no buckets). The FE
   * labels/groups AND counts achieved shares from this field, and NEVER re-derives
   * membership from `disagreements[]` (feedback_metrics_single_source).
   */
  bucket?: ReviewBucket | null;
}

/** Paginated envelope (D4). `count` is rows on this page (no total by design). */
export interface InteractionReviewResponse {
  interaction_type: string;
  days: number;
  location_id: string | null;
  limit: number;
  /**
   * Single-stream offset — present for `drink_promotion` (and single-interaction
   * isolate). For judged types feat-186 replaces this with the three-stream
   * next-offsets below.
   */
  offset?: number;
  count: number;
  items: InteractionReviewItem[];
  // ---- feat-186 (ADR 0001 §D-J) composition echo — judged types only ----
  /** The NORMALIZED requested shares (sum 100), echoed back for the caption. */
  share_discrepancy?: number;
  share_uncertain?: number;
  share_agreement?: number;
  agreement_max_days_back?: number;
  agreement_seed?: string;
  only_current_prompt_template?: boolean;
  /**
   * §D-J cursor pattern — the offsets to send back as `d_offset` / `u_offset` /
   * `a_offset` on the NEXT "load more" (NOT the offsets that were sent). Each
   * stream advances by the number of ITS rows included in this page, so paging is
   * exhaustive-no-gaps for Discrepancy/Uncertain and non-duplicating for Agreement.
   */
  next_d_offset?: number;
  next_u_offset?: number;
  next_a_offset?: number;
  /**
   * §D-J — the load-more signal. True iff ANY stream still has rows past its next
   * offset. Replaces the old `count >= PAGE_SIZE` heuristic, which graceful-degrade
   * breaks (a full page no longer implies more exists). Present on the
   * drink_promotion / isolate envelopes too. The FE hides "load more" on false.
   */
  has_more?: boolean;
  /**
   * feat-188 (ADR 0001 D-5) — the EFFECTIVE exclude-already-reviewed state the
   * server applied, echoed on the judged AND drink_promotion envelopes. Absent on
   * the isolate envelope, where the filter is inert by design (D-2): a deep link
   * must resolve an interaction even when a human already graded it.
   *
   * This is the first-class assertion target for AC1/AC7 — QA reads the ECHO
   * rather than inferring the filter state from the rows (an all-NULL page can
   * happen by chance on a small window).
   */
  exclude_human_reviewed?: boolean;
}

export interface InteractionReviewOptions {
  /** One of the 3 review types — validated server-side (else 422). */
  interactionType: string;
  /** Window in days — {1,7,14,30}, validated server-side. */
  days?: number;
  /** Optional UUID location filter (validated server-side). */
  locationId?: string;
  /** Page size — server default 25, clamped 1..100. */
  limit?: number;
  /** Single-stream offset — drink_promotion + isolate only (judged types use d/u/a). */
  offset?: number;
  // ---- feat-186 (ADR 0001 §D-J) composition params — judged types ----
  /**
   * The three bucket shares (ints, each 0–100, must SUM > 0 → else 422). The server
   * normalizes to sum 100. Sent as `share_discrepancy` / `share_uncertain` /
   * `share_agreement`. Omit for drink_promotion / isolate (inert / ignored).
   */
  shares?: BucketShares;
  /** Agreement spot-check cap (int ≥ 1; default = `days` server-side). */
  agreementMaxDaysBack?: number;
  /** Opaque agreement-sample seed (FE-generated 32-bit int, rides in the URL, D7). */
  agreementSeed?: string;
  /** D5 — restrict to shifts under a currently-active annotation prompt version. */
  onlyCurrentPromptTemplate?: boolean;
  /**
   * feat-196 — "Live clients only", default ON by OMISSION (see
   * {@link setLiveOnlyParam}). Rides the judged and drink_promotion feeds alike:
   * like `exclude_human_reviewed`, it is a POPULATION filter, not a composition
   * param, so it is not gated on `judged`.
   *
   * DELIBERATELY NOT SENT on the single-interaction isolate (`interactionId`).
   *
   * SETTLED (ADR 0001 §2.4, ruling confirmed by product-lead 2026-07-29 with
   * provenance): `t.is_live` is **INERT** on the isolate path — a deep link to a
   * non-live-tenant interaction still resolves, and the isolate envelope does not
   * echo `live_only`. So there is no 404 state to design a recovery for.
   *
   * Why inert: an isolate is a primary-key lookup of one row a human deliberately
   * named. It consumes no queue slot and no judge budget, so filtering buys
   * nothing — while under a default-ON filter every existing deep link to a
   * non-live interaction would start 404-ing, and a 404 here is indistinguishable
   * from "deleted". The reader most likely to follow such a link is an evaluator
   * asking "why is this odd record here?", which is the workflow that motivated
   * this feature.
   *
   * (This ruling was reversed twice while Slice B was in flight. Omitting the param
   * is what made the FE correct throughout: ON is the default, so sending nothing
   * leaves the behaviour entirely server-side and the FE asserts nothing either way.
   * That is still the reason to keep the omission rather than "tidy" it into an
   * explicit value.)
   */
  liveOnly?: boolean;
  /** Three-stream paging offsets (§D-J). Sent back from `next_*` on load-more. */
  dOffset?: number;
  uOffset?: number;
  aOffset?: number;
  /**
   * feat-183 — single-interaction isolate. When present, the backend short-circuits
   * to `WHERE si.id = %s` and returns EXACTLY that one interaction, IGNORING the
   * days window (RBAC/tenant scoping unchanged — a foreign/absent id → 404). Unlike
   * `?focus=` (which deep-links WITHIN the current filters), this pulls an
   * interaction up independent of any filter. Callers omit the composition params
   * when setting this (they're ignored server-side).
   */
  interactionId?: string;
  /**
   * feat-188 (ADR 0001 D-5) — exclude interactions a human has already DECIDED
   * (`si.correct_human_verified IS NOT NULL`). Default **ON**, expressed by
   * OMISSION: leave this `undefined` (or `true`) and the param is not sent, so the
   * backend's `Query(default=True)` applies. Only `false` is transmitted, as the
   * single canonical literal `exclude_human_reviewed=false`.
   *
   * NOT a composition param — unlike `shares` / `agreementMaxDaysBack` (judged-only,
   * inert for drink_promotion), this is a POPULATION filter and callers MUST send it
   * on the drink_promotion feed too (ADR D-2 / AC9). It is never judged there, but it
   * IS human-reviewed via the same feat-131 PUT, so the filter is meaningful — a
   * passing judged-type test proves nothing about that path.
   *
   * Omit for the feat-183 isolate (`interactionId`): the filter is inert there by
   * design, so that a deep link to an already-graded interaction still resolves
   * instead of 404-ing.
   */
  excludeHumanReviewed?: boolean;
}

/**
 * GET /api/admin/interaction-evaluations/review → {@link InteractionReviewResponse}
 * envelope. For JUDGED types (initial_greeting / server_upsell) the page is composed
 * to the requested 3-bucket mix (feat-186 §D-J): sends `share_discrepancy` /
 * `share_uncertain` / `share_agreement`, `agreement_max_days_back`, `agreement_seed`,
 * `only_current_prompt_template`, and the three paging offsets `d_offset` / `u_offset`
 * / `a_offset`. drink_promotion + isolate keep the single `offset`. Throws on non-2xx
 * (incl. 422 on a disallowed type / out-of-range share, and the 404 fail-closed on a
 * foreign/absent `interaction_id`) so the caller surfaces the error. feat-183:
 * passing `interactionId` isolates that one interaction, ignoring the window.
 * feat-188: `excludeHumanReviewed` rides BOTH the judged and drink_promotion feeds
 * (it is a population filter, not a composition param) and is sent only when
 * explicitly `false` — default-ON is expressed by omission (ADR 0001 D-5).
 */
export async function getInteractionReviewList(
  options: InteractionReviewOptions,
): Promise<InteractionReviewResponse> {
  const params = new URLSearchParams();
  params.set('interaction_type', options.interactionType);
  if (options.days != null) params.set('days', String(options.days));
  if (options.locationId) params.set('location_id', options.locationId);
  if (options.limit != null) params.set('limit', String(options.limit));
  // Single-stream offset — drink_promotion + isolate.
  if (options.offset != null) params.set('offset', String(options.offset));
  // feat-186 composition params (judged types). The server ignores them for
  // drink_promotion / isolate (accepted-but-inert, §D-J), so it is harmless if a
  // caller leaves them set, but the component only sends them for judged feeds.
  if (options.shares) {
    params.set('share_discrepancy', String(options.shares.discrepancy));
    params.set('share_uncertain', String(options.shares.uncertain));
    params.set('share_agreement', String(options.shares.agreement));
  }
  if (options.agreementMaxDaysBack != null) {
    params.set('agreement_max_days_back', String(options.agreementMaxDaysBack));
  }
  if (options.agreementSeed != null) params.set('agreement_seed', options.agreementSeed);
  if (options.onlyCurrentPromptTemplate) params.set('only_current_prompt_template', 'true');
  if (options.dOffset != null) params.set('d_offset', String(options.dOffset));
  if (options.uOffset != null) params.set('u_offset', String(options.uOffset));
  if (options.aOffset != null) params.set('a_offset', String(options.aOffset));
  // feat-183 — single-interaction isolate (short-circuits the window server-side).
  if (options.interactionId) params.set('interaction_id', options.interactionId);
  // feat-196 — live-client scope, default-ON by omission. See the `liveOnly` doc
  // on InteractionReviewOptions for why the isolate path deliberately omits it.
  setLiveOnlyParam(params, options.liveOnly);
  // feat-188 (ADR 0001 D-5) — default-ON by OMISSION. Sending nothing means the
  // backend default (True) applies, so a bare/shared URL is filtered without the FE
  // having to assert anything. ONLY the explicit opt-out travels, as the single
  // canonical literal `false` — `=== false` (not falsy) so `undefined` stays silent.
  if (options.excludeHumanReviewed === false) {
    params.set('exclude_human_reviewed', 'false');
  }
  return getObject<InteractionReviewResponse>(
    `${INTERACTION_EVAL_BASE}/review?${params.toString()}`,
  );
}

/**
 * feat-191 slice (e) — the id-isolate PROBE: "has anyone decided this one row?"
 *
 * A named sibling rather than a bare `getInteractionReviewList({interactionId})`
 * call, for two reasons that are both about reviewability:
 *  1. It is a different QUESTION. The list call asks "give me a page of the queue";
 *    this asks about one row by primary key, with the window, composition and
 *    exclude predicates all short-circuited server-side (feat-183 + feat-188 D-2).
 *  2. It keeps the two out of each other's call logs. Several suites assert on what
 *    the LOAD sent (`toHaveBeenLastCalledWith` on the list client); a background
 *    probe sharing that function would silently become "the last call" and those
 *    assertions would start measuring the probe instead — passing or failing for
 *    reasons unrelated to what they are named for.
 *
 * Returns the row, or null when the id is absent from the response. Throws on
 * non-2xx (including the 404 fail-closed on a foreign id) — the caller treats ANY
 * failure as a no-op, because marking a card on a network blip is worse than a
 * stale card.
 */
export async function getInteractionReviewById(
  interactionType: string,
  interactionId: string,
): Promise<InteractionReviewItem | null> {
  const resp = await getInteractionReviewList({
    interactionType,
    interactionId,
    limit: 1,
  });
  return resp.items?.[0] ?? null;
}

// ============================================================================
// feat-160 (ADR 0001) — standalone cross-type disagreements view.
// GET /api/admin/interaction-evaluations/disagreements returns disagreement rows
// ACROSS the judged types (greeting + upsell), newest-first, with the SAME
// require_admin gate + Window/Location semantics (incl. feat-158 1d = calendar
// day) as the eval family. Compact rows — NO transcript excerpt (the reviewer
// drills into the per-type review card via the interaction id for full context).
//
// Envelope mirrors `review` (no total by design; page next when count == limit).
// Each row carries the annotator context, the offline+live judgements, the human
// verification, and the server-classified `disagreements[]` tokens rendered as
// reason chips. Verdicts/tokens rendered verbatim (feedback_metrics_single_source).
// ============================================================================

/**
 * One compact cross-type disagreement row (no transcript). NOTE: unlike the
 * `review` item (which carries a `judgements[]` array), the standalone endpoint
 * returns the two judge rows PRE-SPLIT as `offline` / `live` (each null or a
 * single {@link ReviewJudgement}), per the backend contract.
 */
export interface DisagreementRow {
  id: string;
  shift_id: string;
  interaction_type: string;
  start_time: number;
  end_time: number | null;
  created: string | null;
  data: { reasoning?: string | null; grade?: string | null; [k: string]: unknown } | null;
  correct_human_verified: boolean | null;
  verified_by: string | null;
  verified_at: string | null;
  /** Offline judge row (feat-156), or null if none. */
  offline: ReviewJudgement | null;
  /** Live double-check row (feat-156), or null if none. */
  live: ReviewJudgement | null;
  /** Server-classified pair(s) that fired — at least one (this is the disagreement feed). */
  disagreements: DisagreementToken[];
  server_id?: string | null;
  server_name?: string | null;
  location_name?: string | null;
}

/** Paginated envelope for the standalone disagreements feed. */
export interface DisagreementsResponse {
  days: number;
  location_id: string | null;
  limit: number;
  offset: number;
  count: number;
  items: DisagreementRow[];
}

export interface DisagreementsOptions {
  /** Window in days — {1,7,14,30}, validated server-side. */
  days?: number;
  /** Optional UUID location filter (validated server-side). */
  locationId?: string;
  /** Page size — server default 25, clamped 1..100. */
  limit?: number;
  offset?: number;
  /**
   * feat-196 — "Live clients only", default ON by OMISSION (see
   * {@link setLiveOnlyParam}). Threaded here because AC10 requires the toggle to
   * survive the drill-down into this view, which — unlike `/review/:type` — is
   * reached by a link that carries NO query params today (see
   * InteractionEvaluations.tsx; the `days`/`location_id` drop on that same link is
   * pre-existing and tracked as FU-1, deliberately not fixed here).
   */
  liveOnly?: boolean;
}

/**
 * GET /api/admin/interaction-evaluations/disagreements?days=&location_id=&limit=&offset=
 * → {@link DisagreementsResponse} envelope, newest-first. Throws on non-2xx so the
 * caller surfaces the error.
 */
export async function getInteractionDisagreements(
  options: DisagreementsOptions = {},
): Promise<DisagreementsResponse> {
  const params = new URLSearchParams();
  if (options.days != null) params.set('days', String(options.days));
  if (options.locationId) params.set('location_id', options.locationId);
  if (options.limit != null) params.set('limit', String(options.limit));
  if (options.offset != null) params.set('offset', String(options.offset));
  setLiveOnlyParam(params, options.liveOnly);
  const qs = params.toString();
  return getObject<DisagreementsResponse>(
    `${INTERACTION_EVAL_BASE}/disagreements${qs ? `?${qs}` : ''}`,
  );
}

// ============================================================================
// feat-199 — Recall QA: the double-check's DISCARDED candidates.
//
// GET /api/admin/interaction-evaluations/rejections returns `interaction_judgement`
// rows where `source='live_double_check'` and `verdict='rejected'` — candidates the
// annotator proposed and its own real-time second pass threw away. Spec bucket 3:
// the data has existed since feat-156 and no human has ever looked at it.
//
// THIS IS NOT THE `/review` FEED WITH A FILTER, and the difference is the whole
// point. `/review` pages over `shift_interaction` rows — things the AI decided DID
// happen. A rejected candidate never became a `shift_interaction` row; that is what
// "rejected" MEANS. So the item shape below diverges from InteractionReviewItem in
// three ways that will bite anyone who assumes otherwise (backend contract drop,
// 2026-08-01):
//
//  1. The key is `judgement_id`. There is **no `id` and no `interaction_id`**.
//     Copying a `key={item.id}` across from the review screen yields
//     `key={undefined}`, which React renders without complaint.
//  2. There is **no `correct_human_verified` / `verified_by` / `verified_at` /
//     `data` / `disagreements` / `bucket`** — every one of those is a
//     `shift_interaction` column. The backend deliberately did not synthesize them:
//     a card displaying `correct_human_verified` for a candidate that has no
//     interaction is D4's "zero score impact" boundary blurring by accident rather
//     than by decision.
//  3. `reasoning` and `candidate_excerpt` are DIFFERENT fields and the card needs
//     both. `candidate_excerpt` is what the annotator claimed; `reasoning` is the
//     double-check's own words for discarding it — AC2's centrepiece, and the thing
//     no existing screen has ever shown for a rejection.
//
// D4: nothing in this family writes to `shift_interaction`, touches
// `correct_human_verified`, or changes any scorecard, XP total or leaderboard value.
// ============================================================================

/**
 * The interaction types this queue can serve.
 *
 * NOT the same set as `REVIEW_TYPES`, and **the reason is narrower than "drink
 * promotions aren't double-checked"** — that phrasing is false, and believing it
 * cost this feature a reversal. Traced end-to-end in `restaurant-backend` at
 * `feat-199-recall-qa-unflagged-and-discarded`:
 *
 *   1. `transcript_annotator.py:700` iterates `prompt_config.interaction_types_filter`,
 *      which contains `drink_promotion` UNCONDITIONALLY (`prompt_config.py:310-314`,
 *      "Always include standard types"). So drink promotions DO enter the loop.
 *   2. Every candidate in that loop reaches `InteractionJudgementDB.log(
 *      source=SOURCE_LIVE_DOUBLE_CHECK, …)` at `transcript_annotator.py:477-495`.
 *      So `source='live_double_check'` rows for `drink_promotion` DO get written.
 *   3. **But `_double_check_interaction_no_suppress` short-circuits FIRST**
 *      (`:330-331`): `if interaction["interaction_type"] == "drink_promotion" or
 *      self.demo: return {"confirmed": True, "reasoning": None}` — before any model
 *      call and before any statement that can raise.
 *   4. The wrapper at `:351` therefore returns `{"confirmed": True}` with no `error`
 *      key, `confirmed = v["confirmed"]` is True at `:449`, and the verdict ternary
 *      at `:487-489` resolves to `VERDICT_CONFIRMED`.
 *
 * So drink_promotion IS double-checked in the bookkeeping sense and its verdict is
 * **always `confirmed`** — `rejected` (and `error`) are unreachable for it. This
 * queue filters on `verdict='rejected'`, so a drink_promotion queue is empty
 * FOREVER, not empty today. Both halves matter: (1)-(2) are why "never
 * double-checked" is wrong, (3)-(4) are why excluding it from the picker is right.
 *
 * ONE THING THIS DOES NOT COVER, and it is measurable rather than arguable: the
 * short-circuit governs what the code writes TODAY. If `interaction_judgement` holds
 * historical `drink_promotion` + `rejected` rows from before it existed, the type is
 * not empty over a window that reaches them. Backend owns that count.
 */
// Re-exported from the queries module, which is the single definition. It lives
// there because it is DATA, not I/O: every consumer test mocks THIS module with an
// explicit object, so a constant imported from here comes back `undefined` in those
// suites — which is how a link built on it crashed 10 existing tests.
export {
  REJECTION_REVIEW_TYPES,
  isRejectionReviewType,
} from '../components/restaurant/admin/interactionEvaluationsQueries';
export type { RejectionReviewType } from '../components/restaurant/admin/interactionEvaluationsQueries';

/**
 * The human recall verdict (ADR 0001 Q1). **THREE values, not two.**
 *
 * The spec's two buttons ("the double-check was right" / "this should have been
 * kept") map to `upheld` / `overturned`. `unsure` is the third and it is load-bearing,
 * not a convenience: without an escape hatch a reviewer who genuinely cannot tell is
 * FORCED to guess, and a forced guess enters the overturn rate indistinguishable from
 * a real judgement — which is precisely the silent-measurement failure this whole
 * feature exists to end. `unsure` is excluded from the overturn rate's numerator AND
 * denominator, and its own rate is published (a high one means the review window is
 * too tight to judge from).
 *
 * Clearing a verdict removes the ROW (DB `DELETE`; on the wire it is
 * `PUT {verdict: null}` — see {@link clearRecallVerdict}) rather than storing a
 * fourth value, so "never decided" and "decided, then cleared" are deliberately the
 * same state in the data.
 */
export const RECALL_VERDICTS = ['upheld', 'overturned', 'unsure'] as const;
export type RecallVerdict = (typeof RECALL_VERDICTS)[number];

/**
 * A published rate (ADR 0001 §5). **There is no bare-number form of a rate on this
 * surface — a rate is always this object**, which is what makes AC9 hold by
 * construction rather than by a caption someone remembers to render.
 *
 * `value` is null when the denominator is below the suppression floor (n = 10 decided
 * reviews), with `suppressed_reason` naming why; the COUNTS still render in that
 * state. The floor is derived from the estimator, not from queue volume: at n < 10,
 * 1/n > 0.10, so one more click would move the displayed percentage by more than ten
 * points — the number would be reporting the reviewer's last click rather than the
 * population.
 *
 * RENDER VERBATIM. Never recompute a rate from `numerator`/`denominator` on the FE,
 * and never derive one where the backend sent null — metrics live in the backend
 * (feedback_metrics_single_source). Recomputing is exactly how a suppressed value
 * comes back to life as an authoritative-looking percentage.
 */
export interface RateStat {
  /** null ⇒ suppressed; render the counts and the reason, never a computed stand-in. */
  value: number | null;
  numerator: number;
  denominator: number;
  ci_low: number | null;
  ci_high: number | null;
  /**
   * ADR 0001 §5.2b — WHICH POPULATION this rate was computed over, stated on the wire
   * rather than in a caption (a caption is the thing that gets deleted).
   *
   * Load-bearing, and it exists because of a defect found in §5.2 after it was
   * written. Every feat-199 metric is restricted to `('initial_greeting',
   * 'server_upsell')`. The reason is not tidiness: an auto-confirmed row is
   * **structurally undiscardable** — it can only ever land in the DENOMINATOR of
   * `rejected ÷ (confirmed + rejected)`, never in the numerator. Two classes of row
   * are auto-confirmed with no model involvement (`transcript_annotator.py:330-331`):
   * every `drink_promotion` on EVERY server, and every type on a DEMO server. Include
   * either and the discard rate is **deflated by construction, proportionally** — the
   * more drink promotions a location runs, the better its double-check appears to
   * behave. That is a fake number of the same species as the fake recall percentage
   * TRAP-1 exists to prevent, hiding inside a rate specified as honest.
   */
  population: string;
  /** e.g. `below_min_denominator`. Distinct reasons get DISTINCT copy on screen. */
  suppressed_reason: string | null;
}

/**
 * One discarded candidate. Field-for-field the backend contract; nothing here is
 * recomputed on the FE (feedback_metrics_single_source).
 */
export interface InteractionRejectionItem {
  /**
   * `interaction_judgement.id` — THE KEY for this feed. There is no `id` field and
   * no `interaction_id`: this candidate never became a `shift_interaction`.
   */
  judgement_id: string;
  shift_id: string;
  interaction_type: string;
  /**
   * The candidate span, from the `interaction_judgement` SNAPSHOT columns — not from
   * a `shift_interaction` row, which does not exist for a rejection. The backend
   * feeds exactly these into the same `TranscriptPartDB.get_interaction_span(shift_id,
   * start, end)` the existing review cards and the offline judge use, so the window on
   * this card and the window the judge scored can never diverge (AC3, feat-183).
   */
  start_time: number;
  end_time: number | null;
  created: string | null;
  /**
   * **The EXACT string the double-check was given** — `transcript_annotator.py:492-493`,
   * same helper / args / buffer as the model call (ADR 0001 §3.1).
   *
   * NOT interchangeable with `transcript` below, and the distinction is the card's
   * whole validity. The tight-span transcript is a RECONSTRUCTION of the window and
   * is not provably identical to what the model actually saw. A reviewer asked "was
   * the double-check right to discard this?" while reading text the double-check was
   * never shown is answering a different question — and would answer it confidently.
   * So both render, labelled distinctly; this one is captioned as what the
   * double-check was given.
   */
  candidate_excerpt: string | null;
  /**
   * Why the LIVE DOUBLE-CHECK discarded it, in its own words. AC2 makes this the
   * point of the card: the reviewer is judging this reasoning, not the annotator's.
   */
  reasoning: string | null;
  judge_model: string | null;
  /** AC8 — the metrics panel compares overturn rates across prompt versions. */
  prompt_version_id: string | null;
  /** Same shape as the `/review` transcript, from the same helper. */
  transcript: ReviewTranscriptPart[];
  /**
   * ADR 0001 §3.1 — why the transcript window is what it is. The
   * `interaction_judgement` snapshot columns are NULLABLE, so an empty `transcript`
   * has more than one cause and they are not the same thing to a reviewer:
   *   `ok`           — the window resolved; an empty list means genuinely no parts.
   *   `no_anchor`    — no snapshot start/end to anchor the window to.
   *   `empty_window` — the window resolved but contains no transcript parts.
   * MUST be honoured on the card: an empty window rendered as a blank or broken
   * transcript is the AC10 failure moved down one level — "we could not build the
   * window" and "the window is legitimately empty" reading identically. Optional so
   * the FE degrades if an older row omits it.
   */
  window_status?: 'ok' | 'no_anchor' | 'empty_window' | null;
  /**
   * The human recall review, ECHOED from `recall_review` — **`null` means exactly one
   * thing: nobody has decided this candidate.**
   *
   * That the null is unambiguous is a designed property, not luck: "decided, but
   * undecidable" is the STORED value `unsure`, so it never collapses into absence.
   * This is the same separation the Undo contract rests on — undoing a first decision
   * must CLEAR the row (restore absence) rather than write a neutral stand-in, because
   * a written `unsure` is indistinguishable from one a reviewer chose and `unsure` has
   * its own published rate.
   *
   * AC4 ("persists, survives reload, re-deciding overwrites rather than duplicating")
   * is asserted against THIS echo, never against the FE's own optimistic state — the
   * latter passes happily while the write is silently failing.
   */
  review?: {
    verdict: RecallVerdict;
    reviewed_by: string | null;
    reviewed_at: string | null;
    created: string | null;
    /** Client-reported ms, card-rendered → verdict-sent. NULL ≠ 0 (see the write). */
    decision_latency_ms: number | null;
  } | null;
  server_id?: string | null;
  server_name?: string | null;
  location_name?: string | null;
  /**
   * feat-205 AC16 / D7 — absent until Part B lands. **Absent is not "there were
   * none"**: the empty-list case is `items: []` with the object present, which is a
   * measurement. Render nothing at all when the key is missing.
   */
  neighbours?: RejectionNeighbours;
}

/**
 * Paginated envelope. `live_only` is echoed as the EFFECTIVE value the server
 * applied — assert the echo rather than inferring the filter state from which rows
 * came back, which is what feat-188 learned with `exclude_human_reviewed` (a page
 * that happens to contain only live-tenant rows proves nothing).
 */
/**
 * feat-205 Part B — the coverage envelope (ADR 0001 §4.1).
 *
 * A rejection whose span is already covered by a retained `shift_interaction` on the
 * same shift was a correct machine decision about a moment that was never dropped.
 * Those rows are suppressed from the queue in ROW SELECTION (TRAP-1), and these are
 * the numbers that make the suppression auditable from the response alone.
 *
 * **`total_in_scope`, `suppressed_covered` and `reviewable_total` are population
 * figures and come from one aggregate statement**; the envelope's `count` is still
 * the size of THIS PAGE and is not interchangeable with any of them (AC12 —
 * `shown` is `reviewable_total`, ruled by the architect after backend and frontend
 * independently hit the same ambiguity). Never reconcile against `count`.
 *
 * `null_span_reviewable` is a labelled SUBSET of `reviewable_total` — a candidate
 * with a NULL span can never be shown to be covered, so it stays reviewable — not a
 * fourth partition. Do not add it into a total.
 */
export interface RejectionCoverage {
  applied: boolean;
  /**
   * The coverage rule the server actually ran, echoed so the UI reports it rather than
   * assuming it.
   *
   * **CURRENT VALUE: `transcript_part_v1`, family `transcript_part_` (feat-206 D9,
   * 2026-08-09).** Coverage is a transcript-PART intersection: a retained interaction
   * covers a candidate when the two spans select at least one `transcript_part` in
   * common. There is no threshold left to state.
   *
   * **`epsilon_*` is SUPERSEDED as a RULE, and is still recognised on purpose.** It was
   * a magnitude test — overlap had to exceed `_COVERAGE_EPSILON_S` (0.5 s) — and that
   * constant existed only to discard artifacts of measuring in seconds: 350 candidates
   * (16.1 %) suppressed solely by an overlap of at most 0.43 **ms**, plus zero-length
   * point interactions satisfying an overlap test at exactly zero measure. Both classes
   * disappear structurally under part intersection rather than by tuning. Recognition is
   * kept because ship order across two repos is not atomic: an older backend still emits
   * `epsilon_*` during rollout, and a detector that reds a working deploy is the failure
   * mode, not the guard (spec AC19).
   *
   * **`half_open` is SUPERSEDED and NOT recognised** — superseded by the epsilon before
   * feat-206 superseded that.
   *
   * **MATCH THE FAMILY, NOT THE VALUE.** The emitted string has always been derived from
   * the rule — `f"epsilon_{_COVERAGE_EPSILON_S}s"` then, a versioned family token now —
   * so it changes whenever the rule changes, which is a deliberate contract change, not
   * a regression. **A check pinned to `=== 'epsilon_0.5s'` would cry wolf on every
   * threshold change, and a detector that fires on legitimate changes is one that gets
   * deleted** (architect, ADR §4.1a). Backend pins the exact current value in their own
   * test; that red is their signal to tell us, not ours to discover at runtime — and on
   * feat-206 it is exactly what fired.
   *
   * **Two measurements, and the second is why the first needs its build recorded:**
   *  - 07:03Z 2026-08-03 — wire said `half_open` while the epsilon was what ran. I read
   *    that as a stale label over a current predicate, which was right about the state
   *    and wrong about the cause.
   *  - 07:15Z 2026-08-03, process started 07:07:52Z — wire says **`epsilon_0.5s`** at
   *    both endpoints. Backend fixed it in `bfe1879` and made the label derived, so the
   *    value and its name can no longer drift apart.
   *
   * **The wire is authoritative about what a RUNNING PROCESS emits, not about what the
   * code says** — so a payload claim needs the age of the process beside it, or it is a
   * fact without a date (ADR §2.2.2a). Recording the start time is what let this be
   * resolved rather than argued.
   *
   * **And the variable is the FILE MTIME, not the commit time** — corrected by backend
   * after they measured it, and worth stating because the obvious reasoning is wrong in
   * BOTH directions. My process started 07:07:52Z, **42 seconds BEFORE `bfe1879` was
   * committed**, and still served `epsilon_0.5s`: a dev server imports the working
   * tree, so it picks up an *edit*, not a *commit*. The same process did not have the
   * `population` fix, edited ~07:13. So:
   *  - a process can predate a commit and still serve its fix (this case), and
   *  - an edit newer than the process is invisible to it however long ago it was
   *    committed.
   * An earlier version of this note explained the split by commit time. It reached the
   * right conclusion — instrument age, not a defect — from reasoning that does not hold.
   */
  semantics: string;
  retained_definition: string;
  total_in_scope: number;
  suppressed_covered: number;
  reviewable_total: number;
  null_span_reviewable: number;
  /**
   * feat-206 D11 / D4(a) — candidates suppressed because their span is **ZERO-LENGTH**
   * (`start_time == end_time`), i.e. suppressed WITHOUT being covered by anything.
   *
   * **A labelled SUBSET of `suppressed_covered`, not a fourth partition.** It is already
   * inside that number, so it must never be added into a total — same shape as
   * `null_span_reviewable` on the other side of the split.
   *
   * It exists because D4(a) removes these rows from the only surface that would ever
   * have shown them, and the spec's *Known risk accepted* names exposing the count as
   * the mitigation. **A rise in it is a pipeline alarm, not improved coverage** — a
   * detection with no duration is a detection with no content. Rendered on both screens
   * (design D-b) in terms that cannot be read as good news.
   *
   * **RENAMED from `suppressed_empty_parts` 2026-08-10, when D4 was ruled.** The old
   * name described "selected no transcript part", which AC31 showed is TWO populations:
   * 364 zero-length spans, and 241 positive-length spans (1.28 s–65.74 s) sitting
   * entirely inside one long part — real moments that select no part only because their
   * span begins just after a part start. D4 split accordingly: the first is suppressed
   * and counted here, the second stays REVIEWABLE and is not counted anywhere, because
   * it is in the queue where a human can see it. A field named for the union would now
   * be counting one of the two.
   *
   * **Optional: absent means an older backend, which is NOT zero.** A `0` here asserts a
   * measurement; absence asserts nothing, and the panel renders `—` for it.
   */
  suppressed_zero_length?: number;
  /**
   * The backend's own statement of what this object partitions and how AC8's three
   * states are decided from it. Present on the wire (verified 2026-08-03); typed
   * because a field that exists and is untyped is a field the next reader assumes is
   * absent.
   */
  population?: string;
}

/**
 * feat-205 AC16 / D7 — "what the annotator kept near this span", per item.
 *
 * `gap_seconds` and `overlap_seconds` are both `>= 0` and **at most one is non-zero**.
 * They answer different questions and the UI must not merge them. They are PROXIMITY
 * facts, and they remain true.
 *
 * **What they are NOT, as of feat-206: an input to coverage.** Under `transcript_part_v1`
 * coverage asks whether a `transcript_part` STARTS inside the intersection of the two
 * spans — a fact about the transcript, which no arithmetic on these two durations can
 * recover. A long overlap containing no part start is not coverage. **The client must
 * never classify a neighbour from them; read {@link RejectionNeighbour.shares_part}.**
 *
 * (Superseded, kept as history so nobody re-derives it: under `epsilon_0.5s` the UI read
 * `overlap_seconds > 0.5` as cover, `0 < overlap_seconds <= 0.5` as a FLOAT-rounding
 * sliver — 350 candidates, at most 0.43 **ms** — and `gap_seconds === 0` as an
 * exactly-touching pair. That was a second implementation of the server's predicate
 * living in the browser, which is the drift feat-206 exists to delete.)
 *
 * `radius_seconds` on the enclosing {@link RejectionNeighbours} is a different rule
 * again — the proximity WINDOW these neighbours were selected by — and it is still
 * measured in seconds, correctly (spec AC4, AC30).
 */
export interface RejectionNeighbour {
  interaction_id: string;
  interaction_type: string;
  start_time: number;
  end_time: number | null;
  gap_seconds: number;
  overlap_seconds: number;
  excerpt: string | null;
  /**
   * **The interaction is a POINT, not a span** (`start_time == end_time`) — 8,451 of
   * 114,085 rows, 7.4 % of the table. A point satisfies the overlap test at exactly
   * zero measure, so such a neighbour carries `overlap_seconds: 0` **truthfully**, and
   * 579 of the 938 old sub-millisecond pairs came from these rather than from FLOAT
   * promotion. 183 candidates were suppressed by points alone and are now in the queue,
   * which makes this the **most common** new card — not a corner case.
   *
   * **Branch on THIS FLAG, never on `start_time === end_time`.** The float comparison
   * happens to work today because both endpoints of one row truncate identically on the
   * wire — i.e. it works because of a coincidence in the rendering, which is precisely
   * what the wire-truncation guard exists to prevent. A flag cannot be defeated by
   * rendering (architect, ADR §4.1).
   *
   * **feat-206 makes this MORE true, not less.** Under part intersection a point
   * contains no transcript part, so it cannot cover anything by construction rather
   * than by falling under a threshold.
   */
  zero_length?: boolean;
  /**
   * feat-206 AC27 — **the server's coverage verdict for this neighbour**: does it share
   * at least one transcript part with the candidate?
   *
   * This is the field that replaces the client-side derivation deleted in AC28. It is
   * computed from the SAME SQL fragment as the row-selection predicate, so the per-row
   * statement on the card and the decision to show the card at all cannot disagree —
   * a second implementation drifting from the first is precisely the failure this
   * feature was opened to remove.
   *
   * **`false` for every neighbour on an undecided queue card, by construction.** A card
   * is in the queue only because nothing retained shares a part with it, and
   * `_fetch_neighbours` already restricts to retained interactions. A `true` there is a
   * predicate bug, not a rare state; backend asserts the invariant.
   *
   * **Optional: absent means an older backend.** Absent is not `false` — it is "this
   * response cannot answer", and the UI renders no per-row claim in that case rather
   * than inventing one.
   */
  shares_part?: boolean;
}

export interface RejectionNeighbours {
  radius_seconds: number;
  /** A cap that isn't reported reads as "there was nothing else". Always render it. */
  truncated: boolean;
  /**
   * **A `gap_seconds == 0` neighbour is GUARANTEED to be in this array, even when
   * `truncated` is true — and that guarantee is load-bearing across two repos while
   * being written down in neither.** Pinning it is spec AC32.
   *
   * `_fetch_neighbours` applies a **boundary-preserving cap**: it partitions on
   * `gap_seconds == 0.0`, keeps every such row, and fills the remaining budget from the
   * rest. Its own comment says this is stated as a RULE rather than left as a
   * consequence of the sort order — someone saw the fragility and closed it before
   * anyone here raised it.
   *
   * Why the frontend depends on it: a neighbour that shares a transcript part must have
   * `gap_seconds == 0` (sharing forces a positive-length intersection), so the cap is
   * what makes "no listed neighbour reports `shares_part`" mean "no RETAINED
   * INTERACTION shares a part". The block invariant in `RecallReviewQueue.tsx` rests on
   * exactly that.
   *
   * **CORRECTED 2026-08-10, and the correction is the point.** This docblock previously
   * said the removal condition was "backend pins the ORDERING". That names the wrong
   * property: ordering is not what protects a part-sharer from the cap — the boundary
   * rule is, and it holds under any sort. A removal condition naming a property nothing
   * depends on would never have been satisfied, and the next reader would have inherited
   * it as fact. (Both `design` and I had the mechanism wrong in the same way; team-lead
   * found it by reading `_fetch_neighbours` instead of reasoning about it.)
   *
   * ---
   *
   * **A SECOND, SEPARATE PREMISE LIVES HERE, AND ITS DEPENDENT IS COPY RATHER THAN
   * CODE — which is why it is named explicitly.** Nothing executable relies on it after
   * the invariant gate collapsed to one arm, so it is exactly the kind of note a later
   * cleanup deletes as unused. What breaks if it goes: **the truncation line in
   * `RecallReviewQueue.tsx` — "Showing the *nearest* N — there are more within {r}s."**
   *
   * That sentence is **true today** (measured in the shipped source 2026-08-10; the sort
   * is `(gap_seconds, -overlap_seconds, …)`, i.e. nearest-first) and is **NOT a defect**
   * — do not report it as one. But it is unbacked by this contract, and it was unbacked
   * before feat-206 touched anything.
   *
   * **The property it needs is narrower than "the query is distance-ordered", and the
   * distinction decides whether a test is worth writing** (design, 2026-08-10). The
   * shown set is `boundary + rest[:MAX - len(boundary)]`, and boundary rows are at
   * distance 0 — the nearest anything can be — so they are never the problem. The copy
   * rests entirely on **the ordering of the NON-BOUNDARY REMAINDER**:
   *
   *  - `rest` distance-ordered → the shown set is the nearest N overall → the copy is true.
   *  - `rest` unordered        → the non-boundary rows are an arbitrary subset → false.
   *
   * So an AC32 test that pins only "every `gap == 0` row survives the cap" **passes
   * without touching the part the copy actually rests on.** Pin the remainder's order,
   * or pin nothing and change the copy.
   *
   * **Fallback if that is not pinned**, so this does not stay open forever: reword to
   * *"Showing N of the interactions within {radius}s — there are more."* True under any
   * ordering. It loses something real — "these are the closest" is useful to a reviewer
   * — so the copy is what gives way only if backend declines, not the premise.
   */
  items: RejectionNeighbour[];
}

export interface InteractionRejectionsResponse {
  interaction_type: string;
  days: number;
  location_id: string | null;
  limit: number;
  offset: number;
  live_only: boolean;
  /**
   * Rows on THIS page. No total by design, same as the rest of the family.
   *
   * feat-205: **this is not AC12's `shown`.** Reading it as a population figure makes
   * the reconciliation false on every page but the last, silently.
   */
  count: number;
  /** Explicit — never re-derive it from `count >= limit`. */
  has_more: boolean;
  items: InteractionRejectionItem[];
  /**
   * feat-205 Part B. **OPTIONAL ONLY UNTIL PART B LANDS** — the backend then sends it
   * on EVERY response including empty ones, because it is what makes the three
   * empty-ish states distinguishable (AC8).
   *
   * **Absent is not zero.** Absent means "this build of the API doesn't compute
   * coverage"; zero means "we looked and there were none". Anything reading this must
   * branch on `undefined` before it branches on a number — see `deriveEmptyKind` in
   * `components/restaurant/admin/recallCoverage.ts`, which exists so that branch is
   * written once.
   */
  coverage?: RejectionCoverage;
  /**
   * ADR 0001 — the seed behind this queue's order, echoed so the order is
   * reproducible and assertable rather than inferred.
   *
   * **THIS QUEUE IS NOT NEWEST-FIRST.** Order is a STABLE SEEDED SHUFFLE —
   * `ORDER BY MD5(CONCAT(ij.id, seed))` with a constant seed per type — so any prefix
   * the reviewer completes is a **uniform random sample** of the population rather
   * than an anecdote about the newest rows. That is what makes an overturn rate
   * computed from a partially-cleared queue mean anything at all.
   *
   * CONSEQUENCE FOR THE UI, and it is a copy requirement rather than a nicety: the
   * ordering must be SAID somewhere the reviewer reads, not left to be inferred from
   * row order. Someone who assumes newest-first will read the resulting date gaps as
   * a bug and report the queue as broken.
   *
   * HOW TO GUARD IT — **assert the order line POSITIVELY; do not ban the substring.**
   * An earlier version of this note said "never render 'newest first' anywhere on
   * this surface", and design-lead caught that it would reject their own correct
   * copy: the shipped line is *"Fixed random order — **not** newest first. Stopping
   * partway is fine."* A substring ban cannot tell a CLAIM from its DENIAL, so it
   * fires on the one sentence whose entire job is to prevent the false belief.
   *
   * So the guard is: assert the order line RENDERS and matches /fixed random order/i.
   * A positive assertion cannot be satisfied by silence — a negative one can, and
   * "the forbidden string is absent" is equally true of a page that says nothing
   * about ordering at all, which is the actual failure being guarded against.
   */
  seed?: string | null;
}

export interface InteractionRejectionsOptions {
  /** REQUIRED. One of {@link REJECTION_REVIEW_TYPES}; anything else → 422. */
  interactionType: string;
  /** Window in days — {1,7,14,30}, validated server-side (default 14). */
  days?: number;
  /** Optional UUID location filter (validated server-side). */
  locationId?: string;
  /** Page size — server default 25, clamped 1..100. */
  limit?: number;
  offset?: number;
  /**
   * feat-196 — "Live clients only", DEFAULT ON expressed by OMISSION. See
   * {@link setLiveOnlyParam}: never emit `live_only=true` (one canonical URL per
   * state), never emit a bare `live_only=` (measured 422 bool_parsing), and use
   * `set` not `append` (duplicate keys take the LAST value server-side).
   */
  liveOnly?: boolean;
}

/**
 * GET /api/admin/interaction-evaluations/rejections → {@link InteractionRejectionsResponse}.
 *
 * Throws {@link ScopedReadError} on non-2xx. Callers gate the type on
 * {@link isRejectionReviewType} BEFORE calling, so an out-of-set type is a routing
 * error rather than a 422 to be classified after the fact.
 */
export async function getInteractionRejections(
  options: InteractionRejectionsOptions,
): Promise<InteractionRejectionsResponse> {
  const params = new URLSearchParams();
  params.set('interaction_type', options.interactionType);
  if (options.days != null) params.set('days', String(options.days));
  if (options.locationId) params.set('location_id', options.locationId);
  if (options.limit != null) params.set('limit', String(options.limit));
  if (options.offset != null) params.set('offset', String(options.offset));
  setLiveOnlyParam(params, options.liveOnly);
  return getObject<InteractionRejectionsResponse>(
    `${INTERACTION_EVAL_BASE}/rejections?${params.toString()}`,
  );
}

/**
 * Record (or re-record) the human recall verdict for one discarded candidate.
 * `PUT /api/admin/interaction-evaluations/rejections/{judgement_id}/review`.
 *
 * ADR 0001 §1.5 — an idempotent **UPDATE-in-place**, so re-deciding OVERWRITES and
 * the row id survives (AC4). That identity property is what lets "overwrites rather
 * than duplicating" be asserted on the ROW rather than on a count — a count equality
 * is silently wrong under substitution.
 *
 * **Nothing here writes to `shift_interaction`, touches `correct_human_verified`, or
 * changes any scorecard, XP total or leaderboard value** (D4). This endpoint and
 * {@link clearRecallVerdict} are the ONLY writes in the feat-199 frontend, and they
 * are deliberately NOT the feat-131 `PUT /shift-interactions/{id}` this screen's
 * ergonomics are copied from — copying a screen is exactly how a handler keeps
 * pointing at the old target.
 */
export async function putRecallVerdict(
  judgementId: string,
  verdict: RecallVerdict,
  decisionLatencyMs?: number | null,
): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeader(),
  };
  const path = `${INTERACTION_EVAL_BASE}/rejections/${judgementId}/review`;
  // ADR 0001 §0.3.2 — `decision_latency_ms` is CLIENT-REPORTED, and **NULL and 0 must
  // stay distinguishable**: NULL = "no measurement was taken", 0 = "measured, and it
  // was instantaneous". Collapsing them would delete the very signal the field
  // exists for, since an implausibly-fast decision is the detectable class of
  // mis-click. So the key is OMITTED when we have no measurement, and SENT when we
  // do — including when the measured value is 0. Never coerce, never default to 0,
  // and never use a falsy test here (`if (ms)` drops a real 0).
  const body: { verdict: RecallVerdict; decision_latency_ms?: number } = { verdict };
  if (decisionLatencyMs != null) body.decision_latency_ms = decisionLatencyMs;
  const resp = await authedApiFetch(backendUrl(path), {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new ScopedReadError(path, resp.status, text, parseErrorBody(text));
  }
}

/**
 * Clear a recall verdict — **`PUT` the same path with `{verdict: null}`.**
 *
 * NOT a `DELETE` verb, and the distinction cost a round-trip so it is written down:
 * ADR 0001 §1.6b says "`DELETE` = Clear", which the backend correctly read as
 * ambiguous. The architect confirmed they meant the **DB operation**, not the HTTP
 * verb — *"read as an HTTP verb it makes a fourth route against a count I'd stated as
 * TEN"* (the evaluator-gated endpoint census). So both halves of that sentence hold
 * at once: **HTTP `PUT {verdict: null}` → DB `DELETE FROM recall_review`**, and it
 * matches feat-131's `PUT /shift-interactions/{id}` with `correct_human_verified:
 * null`, which is the muscle memory the ADR explicitly invokes.
 *
 * **This is what Undo calls when the card had NO previous verdict.** Absence-of-row
 * means "never reviewed", so undoing a FIRST decision must return to absence, not
 * write a neutral default. A default would be counted: an `unsure` written by an Undo
 * is indistinguishable from an `unsure` a reviewer chose, and `unsure` has its own
 * published rate — so the Undo would move a number.
 *
 * @returns `changed` — false when there was nothing to clear. Use it: a double-click
 * on Undo must not report that it undid something that wasn't there.
 */
export async function clearRecallVerdict(judgementId: string): Promise<{ changed: boolean }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeader(),
  };
  const path = `${INTERACTION_EVAL_BASE}/rejections/${judgementId}/review`;
  const resp = await authedApiFetch(backendUrl(path), {
    method: 'PUT',
    headers,
    body: JSON.stringify({ verdict: null }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new ScopedReadError(path, resp.status, text, parseErrorBody(text));
  }
  const body = await resp.json().catch(() => ({}));
  // Default TRUE only if the server omitted the field (older build): claiming a
  // change we didn't observe is the lesser error here versus telling the reviewer
  // nothing happened when it did. The assertion that matters is that an explicit
  // `false` is honoured, not coerced.
  return { changed: (body as { changed?: boolean }).changed !== false };
}

/**
 * Restore a card to the verdict it held BEFORE the decision being undone.
 *
 * The whole point of routing Undo through one function: `previous == null` means the
 * card was never decided, and that MUST clear the row rather than write a stand-in.
 * Calling `putRecallVerdict` with some placeholder is the bug this exists to make
 * unavailable — see {@link clearRecallVerdict}.
 *
 * Deliberately does NOT forward a `decision_latency_ms`: an undo is not a decision,
 * and stamping it with a latency would put a machine-speed number into the
 * distribution the implausibly-fast fraction is drawn from — inventing exactly the
 * signal that fraction exists to detect.
 */
export async function undoRecallVerdict(
  judgementId: string,
  previous: RecallVerdict | null,
): Promise<{ changed: boolean }> {
  if (previous == null) return clearRecallVerdict(judgementId);
  await putRecallVerdict(judgementId, previous);
  return { changed: true };
}

// ---------------------------------------------------------------------------
// feat-199 type counts — GET …/rejections/type-counts
// ---------------------------------------------------------------------------

/**
 * Why a type shows nothing. **FOUR values, not three.**
 *
 *  - `in_phase_1`          — reviewable now.
 *  - `deferred`            — rejections EXIST and are deliberately out of Phase 1.
 *  - `cannot_be_rejected`  — auto-confirmed before any model call; impossible, not empty.
 *  - `not_in_phase_1_scope`— a type present in the DATA that is in none of our lists.
 *
 * The fourth is the one to get right, and it is the reason this is a string union
 * with a fallback rather than a three-way switch: it fires for an interaction type
 * that exists in `interaction_judgement` and that this frontend has never heard of.
 * A type nobody enumerated, carrying real rejections, is the strongest possible
 * instance of *"I don't know what I'm not looking at"* — so it must render as an
 * unknown WITH its count, never be dropped and never be styled as "impossible".
 */
export type RejectionTypeState =
  | 'in_phase_1'
  | 'deferred'
  | 'cannot_be_rejected'
  | 'not_in_phase_1_scope';

export interface RejectionTypeCount {
  interaction_type: string;
  /** The TOTAL rejection population for this type. Meaning unchanged by feat-205. */
  rejections: number;
  state: RejectionTypeState | string;
  /**
   * **A BOOLEAN — "this type is served by Phase 1".** It is NOT a count and feat-205
   * does not retype it (ADR 0001 §4.2, called out because the new count field is
   * `reviewable_rejections` and the two names are one word apart). A number arriving
   * here is a defect, not a contract change.
   */
  reviewable: boolean;
  /**
   * feat-205 Part B (D10/AC9). Absent until Part B lands — and **absent is not zero**;
   * a chip must fall back to showing `rejections` alone rather than implying that
   * nothing was suppressed. Never derive one of these by subtracting the other: that
   * turns a backend disagreement into a number that looks fine.
   */
  covered_suppressed?: number;
  /** feat-205 Part B — the chip HEADLINE once present (D10). See above on absence. */
  reviewable_rejections?: number;
}

export interface RejectionTypeCountsResponse {
  days: number;
  location_id: string | null;
  live_only: boolean;
  /**
   * States that this population is **DELIBERATELY WIDER** than `recall-metrics`.
   * **Do not "align" the two.** Narrowing this one makes `deferred` indistinguishable
   * from `cannot_be_rejected` — both would render as "the queue is empty and that's
   * intended", which is the very confusion the counts exist to break. Widening the
   * metrics one re-admits deferred rows into rates they were scoped out of. Two
   * endpoints, two populations, on purpose.
   */
  population: string;
  counts: RejectionTypeCount[];
}

/**
 * Per-type rejection counts for the picker.
 *
 * **The COUNT is the discriminator, not the styling.** `deferred` and
 * `cannot_be_rejected` are both "empty and intended"; the only honest thing
 * separating them is that one has thousands of rejections sitting behind it and the
 * other has none and never can. A reviewer who can't see that number cannot tell
 * "nothing here" from "a great deal here, deliberately not shown to you" — which is
 * Eric's original question reappearing on the screen built to answer it.
 */
export async function getRejectionTypeCounts(
  options: { days?: number; locationId?: string; liveOnly?: boolean } = {},
): Promise<RejectionTypeCountsResponse> {
  const params = new URLSearchParams();
  if (options.days != null) params.set('days', String(options.days));
  if (options.locationId) params.set('location_id', options.locationId);
  setLiveOnlyParam(params, options.liveOnly);
  const qs = params.toString();
  return getObject<RejectionTypeCountsResponse>(
    `${INTERACTION_EVAL_BASE}/rejections/type-counts${qs ? `?${qs}` : ''}`,
  );
}

// ---------------------------------------------------------------------------
// feat-199 metrics panel — GET /api/admin/interaction-evaluations/recall-metrics
// ---------------------------------------------------------------------------

/**
 * Latency distribution over `latency_known`. FIXED BUCKETS, not percentiles, and the
 * reason is §5.2b: the all-types roll-up must be a SUM over the restricted per-type
 * parts, never a fresh query — **counts sum, percentiles don't.** A p50 over the parts
 * can't be reconstructed without re-querying, and re-querying is exactly how the
 * population restriction gets lost. Backend asserts the buckets partition
 * `latency_known` exactly (sum equals it; no gap, no overlap).
 */
export interface LatencyBuckets {
  under_1s: number;
  s1_2: number;
  s2_5: number;
  s5_15: number;
  s15_60: number;
  over_60s: number;
}

/** One row of the metrics panel — per type, or per (type, prompt version), or total. */
export interface RecallMetricsBlock {
  interaction_type?: string;
  prompt_version_id?: string | null;
  counts: Record<string, number>;
  discard_rate: RateStat;
  review_coverage: RateStat;
  overturn_rate: RateStat;
  /**
   * `unsure ÷ (upheld + overturned + unsure)` — "of the cards she decided, how many
   * were undecidable". A DIFFERENT denominator from `overturn_rate`, which excludes
   * `unsure` entirely (ADR §1.6a). Do not flatten them onto one shared caption; the
   * {@link RateStat} shape exists so that mistake is unrepresentable. A high value is
   * the signal the review window is too tight.
   */
  unsure_rate?: RateStat;
  /**
   * Fraction of decisions recorded faster than the cutoff. **Denominator is
   * `latency_known`, NOT `reviewed`** — deliberately. Over all decisions the fraction
   * would FALL as the client stopped reporting latency, i.e. it would look best
   * exactly when the instrument broke. So {@link latency_unreported} must be rendered
   * beside it: a fraction over a population nobody can see is uninterpretable.
   *
   * EVIDENCE, NEVER A GATE. A fast click on an obvious case is legitimate; nothing is
   * auto-excluded on it. Backend keeps a test whose only job is to go red if anyone
   * turns it into a rule.
   */
  implausibly_fast_rate?: RateStat;
  /** Decisions with no latency reported — the part of the population the rate can't see. */
  latency_unreported?: number;
  latency_known?: number;
  latency_buckets?: LatencyBuckets;
  /**
   * The cutoff that drew {@link implausibly_fast_rate}, travelling WITH the fraction
   * rather than living only in the backend — the fraction is uninterpretable without
   * it, and the architect amended the constant to PROVISIONAL on the grounds that
   * *a guard constant never checked against its own distribution is inert while
   * reading as protection*. Render it; don't hard-code a copy of it.
   */
  implausibly_fast_threshold_ms?: number;
  /**
   * A COUNT, not a rate — and it INHERITS `overturn_rate`'s suppression. Otherwise the
   * number the panel refused to publish as a percentage walks back in as an
   * authoritative-looking integer.
   */
  estimated_wrongly_discarded?: {
    value: number | null;
    low: number | null;
    high: number | null;
    /**
     * feat-205 — **renamed from `basis_rejections`, and the rename is the safety
     * mechanism** (ADR 0001 §3.4). The quantity underneath changed: this count is now
     * `overturn_rate × reviewable`, not `overturn_rate × rejected`. At the measured
     * 59.6 % Phase-1 suppression that is a ~2.5× difference in a field that reads as a
     * work order — "how many discards to go re-listen to".
     *
     * **Do not alias the old name back.** Had the key survived, this file would have
     * kept compiling, kept rendering, kept the same caption, and silently shown a
     * number 2.5× too large. A renamed key is *missing*, which is visible; a redefined
     * key is not. If a caption ever surfaces this basis, its wording changes with it —
     * it is the REVIEWABLE rejection count, not all rejections.
     */
    basis_reviewable: number;
    suppressed_reason: string | null;
  };
}

/**
 * `GET …/recall-metrics`. Every rate is a {@link RateStat} — a bare number is
 * unrepresentable on the wire.
 *
 * `ci_low`/`ci_high` are Wilson with a finite-population correction, computed
 * server-side. **Render verbatim — they are NOT a ± you can reconstruct.** The
 * interval is asymmetric near 0% and 100% on purpose; a symmetric one collapses to
 * zero width there and reads as certainty from twelve reviews.
 */
/**
 * feat-205 AC13 — suppression by COVERING type (ADR 0001 §4.3).
 *
 * **These parts do not partition anything.** A candidate covered by several types is
 * counted under each, so `by_covering_type` sums to MORE than `covered`. That is why
 * `by_covering_type_note` is mandatory and must be rendered: a reader who sums the
 * rows builds a denominator that is not a population, and nothing about the display
 * would stop them. The over-sum is the fact, not a rounding artifact.
 */
export interface CoverageSuppression {
  semantics: string;
  retained_definition: string;
  by_covering_type: { covering_type: string; candidates_covered: number }[];
  /** Mandatory to render — see above. Not decoration, and not a caption. */
  by_covering_type_note: string;
  covered: number;
  reviewable: number;
  rejected: number;
  /**
   * feat-206 D11 / D4(a) / AC26 — candidates suppressed because their span is
   * **zero-length**. A labelled SUBSET of `covered`, never a fourth term. See
   * {@link RejectionCoverage.suppressed_zero_length} for why the name changed when D4
   * was ruled, and for what is deliberately NOT counted here (the 241 positive-length
   * candidates inside one part, which stay reviewable).
   *
   * **Deliberately the same name as on {@link RejectionCoverage}**, despite this
   * object's siblings being named `covered`/`reviewable`/`rejected` rather than
   * `suppressed_*`: one greppable name across two endpoints beats per-endpoint naming
   * symmetry (design, routed to backend and accepted as AC26). That held through the
   * rename — both endpoints moved together, which is the property the choice was for.
   *
   * Optional — absent is an older backend, and is NOT zero.
   */
  suppressed_zero_length?: number;
}

/**
 * feat-205 AC15 / TRAP-6 — the cutover (ADR 0001 §3.6).
 *
 * After Part B, `overturn_rate`, `review_coverage` and `estimated_wrongly_discarded`
 * are computed over the REVIEWABLE population; before it they were computed over ALL
 * rejections. **Any trend spanning the cutover is two different denominators wearing
 * one line.** The ADR requires the frontend to render this as a BREAK, never as a
 * footnote — a footnote beside a continuous line loses to the line.
 *
 * `decisions_recorded_on_now_covered_rows` is what makes the break *quantified* rather
 * than merely declared: it is the count of verdicts Shane already recorded on rows the
 * coverage check would now suppress (D8 — prior calls stand and are flagged).
 */
export interface RecallSeriesBreak {
  rule: string;
  effect: string;
  decisions_recorded_on_now_covered_rows: number;
}

export interface RecallMetricsResponse {
  /** feat-205 AC13. Absent until Part B lands — absent is not "nothing was covered". */
  coverage_suppression?: CoverageSuppression;
  /** feat-205 AC15. Absent until Part B lands. */
  series_break?: RecallSeriesBreak;
  days: number;
  location_id: string | null;
  live_only: boolean;
  /** States the §5.2b type restriction on the wire, not in a caption. */
  population: string;
  min_denominator: number;
  by_type_and_version: RecallMetricsBlock[];
  by_type: RecallMetricsBlock[];
  total: RecallMetricsBlock;
  /**
   * The "always auto-confirmed — never discarded" row for `drink_promotion`.
   * **Never `0%`, never omitted** — a `0%` there reads as "never wrongly discards"
   * when the truth is "never runs", which is a fake number of the same species as a
   * fake recall %. `reason` carries the mechanism.
   */
  not_double_checked: { interaction_type: string; reason: string }[];
  /**
   * §5.2d — **COVERAGE IS A FIRST-CLASS FIELD, not prose in `population`.**
   *
   * Every rate on this panel describes **51.4%** of the rejection population, and the
   * backend's own comment states the consequence: *"a panel that renders them without
   * that fact is labelling half the traffic as all of it."* `population` says
   * "in-scope types", which is TRUE and which a reader cannot decode — it does not
   * tell anyone that `missed_upsell`, with thousands of rejections, is excluded.
   *
   * That is a rate wearing a LABEL instead of a DENOMINATOR, on the panel whose whole
   * premise is TRAP-1. So this renders on screen, prominently, beside the numbers it
   * qualifies — not in a caption, for the same reason the denominator isn't one.
   */
  scope_coverage?: {
    in_scope_types: string[];
    deferred_types: string[];
    measured_share_of_rejections: number;
    measured_over_days: number;
    note: string;
  };
  /**
   * How the reviewed subset was drawn. Rendered because a rate over a *sample* means
   * something different from a rate over a census, and the difference is invisible in
   * the number itself.
   */
  sampling_basis?: string;
}

export async function getRecallMetrics(
  options: { days?: number; locationId?: string; liveOnly?: boolean } = {},
): Promise<RecallMetricsResponse> {
  const params = new URLSearchParams();
  if (options.days != null) params.set('days', String(options.days));
  if (options.locationId) params.set('location_id', options.locationId);
  setLiveOnlyParam(params, options.liveOnly);
  const qs = params.toString();
  return getObject<RecallMetricsResponse>(
    `${INTERACTION_EVAL_BASE}/recall-metrics${qs ? `?${qs}` : ''}`,
  );
}

/**
 * WITHDRAWN 2026-08-01, jointly with the backend — a `NEVER_DOUBLE_CHECKED_CODE` +
 * `isNeverDoubleCheckedError` pair lived here, reading a machine-readable `code` out
 * of the 422 body so the UI could tell *"this type is never double-checked"* apart
 * from *"`days=99` is invalid"*. It was built, mutation-tested, and then **deleted on
 * purpose**. Recorded rather than silently removed, because the deletion is the
 * interesting part:
 *
 * The state it discriminated is **impossible, not merely absent.** `drink_promotion`
 * is auto-confirmed before any model call, and git history shows no version of the
 * code could ever have written a `drink_promotion` + `rejected` row — the
 * short-circuit predates the first live-double-check row by 15 months and the file is
 * untouched since. **A backend `code` that nothing reads is worse than no code**: it
 * looks like a supported contract, so the next person wires a branch to it.
 *
 * The one real remaining case — a stale bookmark to `/…/drink_promotion` — is handled
 * a layer earlier and more cheaply: {@link isRejectionReviewType} rejects an
 * out-of-set type as a ROUTING error **before any fetch is issued**, so that URL never
 * reaches the backend and there is no 422 to classify. `ScopedReadError` keeps its
 * `status` + parsed `body` regardless; that is generally useful and unrelated.
 */
